import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "../../../../shared/common/ThemeContext.jsx";
import { BarChart3, Brain, Activity, ClipboardList, Gauge, ArrowUpDown, Flame, AlertTriangle, Timer, Pencil, Check, X, Loader2, ChevronLeft, ChevronRight, LayoutList, Trash2, History } from "lucide-react";
import TeamPerformanceCharts from "../../../../shared/overview/PerformanceCharts.jsx";
import { useNavigate } from "react-router-dom";
import { prefetchPlayerDetailsData, prefetchPlayerDetailsPage } from "../../../../shared/players/playerDetailsCache";
import { fetchAuth } from '../../../../shared/common/api.jsx';
import { fetchAlerts } from "../../../../shared/common/alertsApi.js";
import { useMe } from "../../../../shared/common/useMe";
import { hasRestrictedClubUserRole } from "../../../../shared/common/roleAccess";
import { useSeason, getCurrentSeasonLabel } from "../../../../shared/common/SeasonContext.jsx";
import TeamOverviewMenu from "../../../../shared/common/TeamOverviewMenu.jsx";
import { DeltaChip } from "../../../../shared/common/DeltaChip.jsx";
import MetricRankingCard from "../../../../shared/common/MetricRankingCard.jsx";
import { MetricRankingListCard } from "../../../../shared/common/MetricRankingListCard.jsx";
import { usePlayersPerf } from "../../../../shared/common/PlayersPerfContext.jsx";
import AvailabilityCompactCard from "../../../../shared/overview/AvailabilityCompactCard.jsx";
import { encodeId } from "../../../../shared/common/hashid.js";
import RPESessionModal from "../../../../shared/mobile/RPESessionModal.jsx";
import AppNoticeDialog from "../../../../shared/common/AppNoticeDialog.jsx";
import ExternalLoadHistograms from "../../../../shared/overview/ExternalLoadHistograms.jsx";
import InternalLoadHistograms from "../../../../shared/overview/InternalLoadHistograms.jsx";
import TrainabilityRankingSection from "../../../../shared/overview/TrainabilityRankingSection.jsx";
import MetricVisibilitySidebar from "../../../../shared/overview/MetricVisibilitySidebar.jsx";
import SubjectiveFeedbackOverview from "../../../../shared/overview/SubjectiveFeedbackOverview.jsx";
import RpeFeedbackOverview from "../../../../shared/overview/RpeFeedbackOverview.jsx";
import RpeSessionManageBar from "../../../../shared/overview/RpeSessionManageBar.jsx";
import RpeSportDayNavigator from "../../../../shared/overview/RpeSportDayNavigator.jsx";
import {
  normalizeRpeSessionId,
  resolveRpeSessionSelection,
  sortRpeSessionsChronologically,
  mergeRpeSessionsForDay,
  currentRpeSportDayIso,
  RPE_TREND_HISTORY_DAYS,
} from "../../../../shared/overview/rpeOverviewUtils.js";
import { buildHooperOverviewPlayers, hasSubjectiveResponseAnswer } from "../../../../shared/overview/feedbackOverviewPlayerScope.js";
import { formatLocalDateTime, formatLocalTime, toDatetimeLocalInputValue } from "../../../../shared/common/datetimeUtils.js";
import {
  PLAYER_POSITIONS,
  normalizePlayerPosition,
} from "../../../../shared/common/PlayerPositions";
import {
  getTrainabilityScore10,
  hasTrainability,
  formatTrainabilityScore,
  getTrainabilityDisplayColor,
  getTrainabilityTextStyle,
  resolveTrainabilityLevel,
} from "../../../../shared/common/TrainabilityUtils";
import {
  getTrainabilityLevelColor,
  getTrainabilityLevelTextColor,
} from "../../../../shared/common/TrainabilityLevels";
import {
  buildActiveAlertPlayerIdSet,
  DEFAULT_TEAM_OVERVIEW_RISK_MODE,
  hasLoadSimulationData,
  isPlayerAtRisk,
  PLAYER_AT_RISK_TRAINABILITY_THRESHOLD,
  readTeamOverviewRiskMode,
  TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY,
} from "../../../../shared/common/playerRiskUtils.js";
// import GlobalStatsTab from "../../../../shared/overview/GlobalStatsTab.jsx";
import { STORAGE_PREFIX } from "@config";

const TRAIN_LOW_10 = 4;
const TRAIN_HIGH_10 = 6;
const TRAINABILITY_TARGET_10 = 7;
const AT_RISK_TRAINABILITY_THRESHOLD = PLAYER_AT_RISK_TRAINABILITY_THRESHOLD;
const RISK_MODE_GLOBAL = "global";
const RISK_MODE_AT_RISK = "at-risk";
const ALERTS_REFRESH_INTERVAL_MS = 60 * 1000;
const FEEDBACK_VIEW_OVERVIEW = "overview";
const FEEDBACK_VIEW_EDIT = "edit";
const FEEDBACK_HISTORY_DAYS = 14;
const LIMITED_INTERNAL_METRIC_KEYS = ["trainability_index", "mechanical_fatigue"];
const LIMITED_INTERNAL_METRIC_SET = new Set(LIMITED_INTERNAL_METRIC_KEYS);

function toIsoDate(value) {
  if (!value || value === "N/A") return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

function toTimestamp(value) {
  if (!value || value === "N/A") return Number.NEGATIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function hasOverviewSimulation(player) {
  if (!player || typeof player !== "object") return false;
  const value = player.timeinfo_date;
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  return normalized !== "" && normalized !== "N/A";
}

function matchesTargetDate(item, targetDate, dateKeys = []) {
  if (!targetDate || !Array.isArray(dateKeys) || dateKeys.length === 0) return true;

  for (const key of dateKeys) {
    const isoDate = toIsoDate(item?.[key]);
    if (!isoDate) continue;
    return isoDate === targetDate;
  }

  return true;
}

function keepLatestByPlayer(
  items,
  { playerKey = "player_id", timeKeys = [], targetDate = null, dateKeys = [] } = {}
) {
  if (!Array.isArray(items)) return [];

  const byPlayer = new Map();
  items.forEach((item) => {
    if (!item || typeof item !== "object") return;
    if (!matchesTargetDate(item, targetDate, dateKeys)) return;

    const playerId = String(item?.[playerKey] ?? "").trim();
    if (!playerId) return;

    const timestamp = timeKeys.reduce(
      (best, key) => Math.max(best, toTimestamp(item?.[key])),
      Number.NEGATIVE_INFINITY
    );

    const current = byPlayer.get(playerId);
    if (!current || timestamp >= current.timestamp) {
      byPlayer.set(playerId, { item, timestamp });
    }
  });

  return Array.from(byPlayer.values()).map((entry) => entry.item);
}

const Card = ({ children, isDark }) => (
  <div className={`rounded-2xl shadow-lg overflow-hidden ring-1 ${
    isDark ? "bg-gray-800 ring-white/10" : "bg-white ring-black/10"
  }`}>
    {children}
  </div>
);

const TableHead = ({ isDark, children }) => (
  <thead className={`${isDark ? "bg-gradient-to-r from-gray-800 to-gray-700" : "bg-gradient-to-r from-gray-100 to-white"}`}>
    <tr className={`${isDark ? "text-gray-200" : "text-gray-700"} text-xs uppercase tracking-wider`}>
      {children}
    </tr>
  </thead>
);

function getChoiceOptionValue(option) {
  if (!option || typeof option !== "object") return option;
  return option.value ?? option.id ?? option.key ?? option.label;
}

function getChoiceOptionLabel(option) {
  if (!option || typeof option !== "object") return option;
  return option.label ?? option.name ?? option.title ?? getChoiceOptionValue(option);
}

function normalizeChoiceOptions(options = []) {
  return (options || [])
    .map((option) => {
      const value = getChoiceOptionValue(option);
      if (value === null || value === undefined || value === "") return null;
      return {
        value: String(value),
        label: String(getChoiceOptionLabel(option) ?? value),
      };
    })
    .filter(Boolean);
}

function isEmptyChoiceValue(value) {
  if (Array.isArray(value)) return value.length === 0;
  return value === undefined || value === null || value === "";
}

function formatChoiceAnswer(value, options = [], fallbackLabels = []) {
  const optionMap = new Map(normalizeChoiceOptions(options).map((option) => [option.value, option.label]));
  const fallbackValues = Array.isArray(fallbackLabels) ? fallbackLabels : [fallbackLabels];
  const selectedValues = Array.isArray(value) ? value : isEmptyChoiceValue(value) ? [] : [value];

  return selectedValues
    .map((entry, index) => {
      const key = String(getChoiceOptionValue(entry));
      return optionMap.get(key) || fallbackValues[index] || getChoiceOptionLabel(entry) || key;
    })
    .filter(Boolean)
    .join(", ");
}

/* ─────────── Inline-editable cell for Hooper / survey answers ─────────── */
const EditableCell = ({ value, previousValue, question, responseId, playerId, templateId, isDark, cellClassName, onSaved, onResponseCreated, targetDate, sessionContext }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const qType = question?.type;
  const cfg = question?.config || {};
  const qMin = cfg.min ?? 0;
  const qMax = cfg.max ?? 10;
  const choiceOptions = normalizeChoiceOptions(question?.options || []);
  const isMultiChoice = qType === "choice" && Number(cfg.maxSelections || 1) > 1;

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const validate = (v) => {
    if (qType === "scale") {
      const n = Number(v);
      if (isNaN(n)) return t('validation.must_be_number');
      if (n < qMin) return `${t('validation.min')}: ${qMin}`;
      if (n > qMax) return `${t('validation.max')}: ${qMax}`;
    }
    if (qType === "yesno" && v !== true && v !== false && v !== "true" && v !== "false") {
      return t('validation.must_be_yes_no');
    }
    return null;
  };

  const handleSave = async () => {
    let finalValue = draft;
    if (qType === "scale") finalValue = Number(draft);
    if (qType === "yesno") finalValue = draft === "true" || draft === true;
    if (qType === "bodymap") {
      finalValue = draft;
    }
    if (qType === "time") {
      finalValue = new Date(draft).toISOString();
    }

    const err = validate(finalValue);
    if (err) { setError(err); return; }

    setSaving(true);
    setError(null);
    try {
      let res;
      if (responseId) {
        res = await fetchAuth(`/surveys/response/${encodeId(responseId)}/answer`, {
          method: "PATCH",
          body: JSON.stringify({ question_id: question.id, value: finalValue }),
        });
      } else {
        res = await fetchAuth(`/surveys/response/inline`, {
          method: "POST",
          body: JSON.stringify({
            template_id: templateId,
            player_id: playerId,
            question_id: question.id,
            value: finalValue,
            ...(targetDate ? { date: targetDate } : {}),
            ...(sessionContext?.session_key ? { session_key: sessionContext.session_key } : {}),
            ...(sessionContext?.submitted_at ? { session_submitted_at: sessionContext.submitted_at } : {}),
            ...(sessionContext?.period ? { period: sessionContext.period } : {}),
            ...(sessionContext?.linked_rpe_session_id ? { linked_rpe_session_id: sessionContext.linked_rpe_session_id } : {}),
          }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Error ${res.status}`);
      }
      const body = await res.json();
      if (!responseId && body.response_id) {
        onResponseCreated?.(body.response_id, question.id, finalValue, sessionContext);
      } else {
        onSaved?.(question.id, finalValue);
      }
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!responseId) {
      setEditing(false);
      setDraft(value ?? "");
      setError(null);
      return;
    }

    const confirmed = window.confirm(
      t("team_overview.confirm_delete_hooper_answer", {
        defaultValue: "Delete this Hooper answer?",
      })
    );
    if (!confirmed) return;

    setDeleting(true);
    setError(null);
    try {
      const res = await fetchAuth(`/surveys/response/${encodeId(responseId)}/answer`, {
        method: "DELETE",
        body: JSON.stringify({ question_id: question.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Error ${res.status}`);
      }

      onSaved?.(question.id, null);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && qType !== "bodymap") handleSave();
    if (e.key === "Escape") { setEditing(false); setDraft(value ?? ""); setError(null); }
  };

  const handleStartEdit = (e) => {
    e.stopPropagation();
    if (qType === "bodymap") {
      setDraft(Array.isArray(value) ? JSON.parse(JSON.stringify(value)) : []);
    } else if (qType === "time") {
      setDraft(toDatetimeLocalInputValue(value || new Date()));
    } else if (qType === "choice" && isMultiChoice) {
      setDraft(Array.isArray(value) ? value : isEmptyChoiceValue(value) ? [] : [value]);
    } else if (qType === "choice" && isEmptyChoiceValue(value)) {
      setDraft(choiceOptions[0]?.value ?? "");
    } else if (qType === "yesno" && (value === undefined || value === null || value === "")) {
      setDraft("true");
    } else {
      setDraft(value ?? "");
    }
    setError(null);
    setEditing(true);
  };

  if (!editing) {
    if (qType === "bodymap") {
      const items = Array.isArray(value) ? value : [];
      const sevColor = (s) => {
        const n = Number(s) || 0;
        if (n >= 8) return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
        if (n >= 5) return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
        if (n >= 1) return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
        return isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500";
      };
      return (
        <td className={`${cellClassName} group relative text-center`}>
          {items.length > 0 ? (
            <div className="flex flex-col gap-0.5 items-center justify-center">
              {items.map((e, i) => (
                <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${sevColor(e.severity)}`}>
                  {e.part} · {e.side} · {e.type} · {Number(e.severity) || 0}/10
                </span>
              ))}
            </div>
          ) : (
            <span>{t('common.na')}</span>
          )}
          <button
            onClick={handleStartEdit}
            className={`absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
              isDark ? "hover:bg-gray-600" : "hover:bg-gray-200"
            }`}
            title={t('common.edit')}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </td>
      );
    }

    let displayed = value !== undefined && value !== null && value !== "" ? value : t('common.na');
    if (qType === "yesno" && typeof value === "boolean") displayed = value ? t('common.yes') : t('common.no');
    if (qType === "choice") displayed = formatChoiceAnswer(value, choiceOptions, question?.choiceFallbackLabels) || t('common.na');
    if (qType === "time" && typeof value === "string") {
      displayed = formatLocalDateTime(value);
    }
    const scaleDelta = (() => {
      if (qType !== "scale") return null;
      const curr = Number(value);
      const prev = Number(previousValue);
      if (!Number.isFinite(curr) || !Number.isFinite(prev)) return null;
      return curr - prev;
    })();

    return (
      <td className={`${cellClassName} group relative`}>
        <div className="flex flex-col items-center gap-0.5">
          <div>
            <span>{displayed}</span>
            {question?.type === "scale" && typeof value === "number" && qMax && (
              <span className="text-xs text-gray-500"> / {qMax}</span>
            )}
          </div>
          {scaleDelta !== null && (
            <DeltaChip
              delta={scaleDelta}
              decimals={1}
              label={t('common.vs_prev')}
            />
          )}
        </div>
        <button
          onClick={handleStartEdit}
          className={`absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
            isDark ? "hover:bg-gray-600" : "hover:bg-gray-200"
          }`}
          title={t('common.edit')}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </td>
    );
  }

  // ─── Bodymap edit mode ───
  if (qType === "bodymap") {
    const entries = Array.isArray(draft) ? draft : [];
    const updateEntry = (idx, field, val) => {
      setDraft(prev => prev.map((e, i) => i === idx ? { ...e, [field]: val } : e));
    };
    const removeEntry = (idx) => {
      setDraft(prev => prev.filter((_, i) => i !== idx));
    };
    const addEntry = () => {
      setDraft(prev => [...prev, { part: "Hamstrings", side: "Left", type: "Pain", severity: 0 }]);
    };
    const MUSCLE_GROUPS = [
      "Hamstrings", "Quadriceps", "Adductors", "Calf",
      "Glutes", "Ankle/Foot", "Knee", "Hip",
      "Shin", "Thigh", "Abs", "Back", "Shoulder", "Other",
    ];
    return (
      <td className={`px-2 py-1 ${isDark ? "bg-gray-700" : "bg-blue-50"}`} onClick={(e) => e.stopPropagation()}>
        <div className="space-y-1 max-w-xs">
          {entries.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-1 text-xs">
              <select
                value={entry.part}
                onChange={(e) => updateEntry(idx, "part", e.target.value)}
                className={`w-32 rounded px-1 py-0.5 border ${isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"}`}
              >
                <option value="" disabled>{t('bodymap.zone')}</option>
                {MUSCLE_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={entry.side} onChange={(e) => updateEntry(idx, "side", e.target.value)}
                className={`rounded px-1 py-0.5 border ${isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"}`}>
                <option value="Left">{t('bodymap.left')}</option>
                <option value="Right">{t('bodymap.right')}</option>
                <option value="Center">{t('bodymap.center')}</option>
              </select>
              <select value={entry.type} onChange={(e) => updateEntry(idx, "type", e.target.value)}
                className={`rounded px-1 py-0.5 border ${isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"}`}>
                <option value="Pain">{t('bodymap.pain')}</option>
                <option value="Soreness">{t('bodymap.soreness')}</option>
                <option value="Fatigue">{t('bodymap.fatigue')}</option>
              </select>
              <input
                type="number"
                min={0}
                max={10}
                step={1}
                value={entry.severity}
                onChange={(e) => updateEntry(idx, "severity", Number(e.target.value))}
                className={`w-12 rounded px-1 py-0.5 border text-center ${isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"}`}
              />
              <button onClick={() => removeEntry(idx)} className="text-red-500 hover:text-red-700 p-0.5" title={t('bodymap.remove')}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-1 mt-1">
            <button onClick={addEntry}
              className={`text-[10px] px-2 py-0.5 rounded border ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
              {t('bodymap.add')}
            </button>
            <button onClick={handleSave} disabled={saving}
              className="p-1 rounded text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50" title={t('common.save')}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </button>
            {responseId && (
              <button
                onClick={handleDelete}
                disabled={deleting || saving}
                className="p-1 rounded text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50"
                title={t('common.delete', { defaultValue: 'Delete' })}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </button>
            )}
            <button onClick={() => { setEditing(false); setDraft(value ?? ""); setError(null); }}
              className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30" title={t('common.cancel')}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {error && <div className="text-red-500 text-[10px] mt-0.5">{error}</div>}
      </td>
    );
  }

  // ─── Standard edit mode ───
  return (
    <td className={`px-2 py-1 ${isDark ? "bg-gray-700" : "bg-blue-50"}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        {qType === "yesno" ? (
          <select
            ref={inputRef}
            value={String(draft)}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-full rounded px-2 py-1 text-sm border ${
              isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
            }`}
          >
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </select>
        ) : qType === "choice" && isMultiChoice && choiceOptions.length ? (
          <div className={`max-h-32 w-full overflow-y-auto rounded border px-2 py-1 text-sm ${
            isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
          }`}>
            {choiceOptions.map((option) => {
              const draftValues = Array.isArray(draft) ? draft.map(String) : [];
              const checked = draftValues.includes(String(option.value));
              return (
                <label key={option.value} className="flex items-center gap-2 py-0.5 text-left">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setDraft((prev) => {
                        const values = Array.isArray(prev) ? prev.map(String) : [];
                        return values.includes(String(option.value))
                          ? values.filter((entry) => entry !== String(option.value))
                          : [...values, String(option.value)];
                      });
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })}
          </div>
        ) : qType === "choice" && choiceOptions.length ? (
          <select
            ref={inputRef}
            value={String(draft)}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-full rounded px-2 py-1 text-sm border ${
              isDark ? "bg-gray-800 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
            }`}
          >
            {choiceOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : qType === "time" ? (
          <input
            ref={inputRef}
            type="datetime-local"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-44 rounded px-2 py-1 text-sm border ${
              error ? "border-red-500" : isDark ? "border-gray-600" : "border-gray-300"
            } ${isDark ? "bg-gray-800 text-white" : "bg-white text-black"}`}
          />
        ) : (
          <input
            ref={inputRef}
            type={qType === "scale" ? "number" : "text"}
            min={qType === "scale" ? qMin : undefined}
            max={qType === "scale" ? qMax : undefined}
            step={qType === "scale" ? "any" : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-20 rounded px-2 py-1 text-sm border ${
              error ? "border-red-500" : isDark ? "border-gray-600" : "border-gray-300"
            } ${isDark ? "bg-gray-800 text-white" : "bg-white text-black"}`}
          />
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-1 rounded text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50"
          title={t('common.save')}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </button>
        {responseId && (
          <button
            onClick={handleDelete}
            disabled={deleting || saving}
            className="p-1 rounded text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50"
            title={t('common.delete', { defaultValue: 'Delete' })}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        )}
        <button
          onClick={() => { setEditing(false); setDraft(value ?? ""); setError(null); }}
          className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
          title={t('common.cancel')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {error && <div className="text-red-500 text-[10px] mt-0.5">{error}</div>}
    </td>
  );
};

/* ─────────── Inline-editable cell for RPE value ─────────── */
const EditableRpeCell = ({
  value,
  previousValue,
  feedbackId,
  feedbackSessionId,
  playerId,
  isDark,
  colorClass,
  onSaved,
  targetDate,
  sessionId,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleSave = async () => {
    const n = Number(draft);
    if (isNaN(n)) { setError(t('validation.must_be_number')); return; }
    if (n < 1 || n > 10) { setError(t('validation.between_1_10')); return; }
    if (targetDate && !sessionId) {
      setError(t("feedback.coach_rpe_session_required", { defaultValue: "Select an RPE session first." }));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let res;
      const useSessionScopedSave = Boolean(sessionId && targetDate);
      if (useSessionScopedSave) {
        res = await fetchAuth(`/rpe_feedback/inline`, {
          method: "POST",
          body: JSON.stringify({
            player_id: playerId,
            rpe_value: n,
            date: targetDate,
            session_id: sessionId,
          }),
        });
      } else if (feedbackId) {
        res = await fetchAuth(`/rpe_feedback/${encodeId(feedbackId)}`, {
          method: "PATCH",
          body: JSON.stringify({ rpe_value: n }),
        });
      } else {
        if (!sessionId) {
          setError(t("feedback.coach_rpe_session_required", { defaultValue: "Select an RPE session first." }));
          setSaving(false);
          return;
        }
        res = await fetchAuth(`/rpe_feedback/inline`, {
          method: "POST",
          body: JSON.stringify({
            player_id: playerId,
            rpe_value: n,
            date: targetDate,
            session_id: sessionId,
          }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Error ${res.status}`);
      }
      const body = await res.json();
      onSaved?.(n, body.feedback_id || feedbackId);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const feedbackBelongsToSession =
      !sessionId ||
      !feedbackSessionId ||
      String(feedbackSessionId) === String(sessionId);

    if (!feedbackId || !feedbackBelongsToSession) {
      setEditing(false);
      setDraft(value ?? "");
      setError(null);
      return;
    }

    const confirmed = window.confirm(
      t("team_overview.confirm_delete_rpe", {
        defaultValue: "Delete this RPE value?",
      })
    );
    if (!confirmed) return;

    setDeleting(true);
    setError(null);
    try {
      const res = await fetchAuth(`/rpe_feedback/${encodeId(feedbackId)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Error ${res.status}`);
      }

      onSaved?.(null, null);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") { setEditing(false); setDraft(value ?? ""); setError(null); }
  };

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setDraft(value ?? "");
    setError(null);
    setEditing(true);
  };

  if (!editing) {
    const rpeDelta = (() => {
      const curr = Number(value);
      const prev = Number(previousValue);
      if (!Number.isFinite(curr) || !Number.isFinite(prev)) return null;
      return curr - prev;
    })();

    return (
      <td className={`px-4 py-3 text-center ${colorClass} group relative`}>
        <div className="flex flex-col items-center gap-0.5">
          <span>{value !== null && value !== undefined ? value : t('common.na')}</span>
          {rpeDelta !== null && (
            <DeltaChip delta={rpeDelta} decimals={1} label={t('common.vs_prev')} />
          )}
        </div>
        <button
          onClick={handleStartEdit}
          className={`absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
            isDark ? "hover:bg-gray-600" : "hover:bg-gray-200"
          }`}
          title={t('common.edit')}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </td>
    );
  }

  return (
    <td className={`px-2 py-1 ${isDark ? "bg-gray-700" : "bg-blue-50"}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-center gap-1">
        <input
          ref={inputRef}
          type="number"
          min={1}
          max={10}
          step="0.5"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`w-16 rounded px-2 py-1 text-sm text-center border ${
            error ? "border-red-500" : isDark ? "border-gray-600" : "border-gray-300"
          } ${isDark ? "bg-gray-800 text-white" : "bg-white text-black"}`}
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-1 rounded text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50"
          title={t('common.save')}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </button>
        {feedbackId && (
          <button
            onClick={handleDelete}
            disabled={deleting || saving}
            className="p-1 rounded text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50"
            title={t('common.delete', { defaultValue: 'Delete' })}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        )}
        <button
          onClick={() => { setEditing(false); setDraft(value ?? ""); setError(null); }}
          className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
          title={t('common.cancel')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {error && <div className="text-red-500 text-[10px] mt-0.5 text-center">{error}</div>}
    </td>
  );
};

const EditableCoachRpeReference = ({ value, sessionId, isDark, onSaved }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const handleSave = async () => {
    const n = Number(draft);
    if (isNaN(n)) {
      setError(t("validation.must_be_number"));
      return;
    }
    if (n < 1 || n > 10) {
      setError(t("validation.between_1_10"));
      return;
    }

    if (!sessionId) {
      setError(t("feedback.coach_rpe_session_required", { defaultValue: "Select an RPE session first." }));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await fetchAuth(`/rpe_sessions/${encodeId(sessionId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coach_rpe_value: n }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Error ${res.status}`);
      }
      const body = await res.json();
      onSaved?.(body.coach_rpe_value ?? n);
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") handleSave();
    if (event.key === "Escape") {
      setEditing(false);
      setDraft(value ?? "");
      setError(null);
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${
        isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50"
      }`}
    >
      <div>
        <p className={`text-xs uppercase tracking-wide ${isDark ? "text-amber-300" : "text-amber-700"}`}>
          {t("feedback.coach_rpe_reference", { defaultValue: "RPE coach" })}
        </p>
        <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
          {t("feedback.coach_rpe_reference_edit_hint", {
            defaultValue: "Note de reference definie lors de la creation de la session.",
          })}
        </p>
      </div>

      {!sessionId ? (
        <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {t("feedback.coach_rpe_session_required", { defaultValue: "Select an RPE session first." })}
        </span>
      ) : !editing ? (
        <div className="flex items-center gap-2">
          <span className="text-2xl font-semibold tabular-nums">
            {value !== null && value !== undefined ? value : t("common.na")}
          </span>
          <button
            type="button"
            onClick={() => {
              setDraft(value ?? "");
              setError(null);
              setEditing(true);
            }}
            className={`rounded-lg p-2 transition ${
              isDark ? "hover:bg-gray-700/70" : "hover:bg-amber-100"
            }`}
            title={t("common.edit")}
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              type="number"
              min={1}
              max={10}
              step="0.5"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-20 rounded px-2 py-1 text-sm text-center border ${
                error ? "border-red-500" : isDark ? "border-gray-600" : "border-gray-300"
              } ${isDark ? "bg-gray-800 text-white" : "bg-white text-black"}`}
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="p-1 rounded text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50"
              title={t("common.save")}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setDraft(value ?? "");
                setError(null);
              }}
              className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
              title={t("common.cancel")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {error ? <div className="text-red-500 text-[10px]">{error}</div> : null}
        </div>
      )}
    </div>
  );
};

const safeDelta = (curr, prev) => {
  const c = Number(curr);
  const p = Number(prev);
  if (!Number.isFinite(c) || !Number.isFinite(p)) return null;
  return c - p;
};

const TeamOverview = () => {
  const { t, i18n } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [columnTitles, setColumnTitles] = useState([]);
  const {
    players,
    allPlayers,
    loading,
    mostRecentSimulation,
    error,
    isFallbackData,
    // isNationalTeam,
  } = usePlayersPerf();
  const { season, setSeason } = useSeason();
  const playersForOverview = useMemo(() => {
    const sourcePlayers =
      Array.isArray(allPlayers) && allPlayers.length > 0
        ? allPlayers
        : Array.isArray(players)
          ? players
          : [];

    return sourcePlayers.filter((player) => player?.player_enabled === true);
  }, [allPlayers, players]);
  const [responses, setResponses] = useState([]);
  const [previousResponses, setPreviousResponses] = useState([]);
  const [displayedPlayers, setDisplayedPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const { isDarkMode } = useTheme();
  const { me } = useMe();
  const hasLimitedClubUserAccess = !me || hasRestrictedClubUserRole(me);
  const [selectedPosition, setSelectedPosition] = useState("All");
  const feedbackOverviewPlayers = useMemo(
    () =>
      buildHooperOverviewPlayers({
        playersForOverview,
        responses,
        selectedPosition,
      }),
    [playersForOverview, selectedPosition, responses]
  );
  // Le back-end borne désormais la "dernière simulation" à la saison sélectionnée :
  // ce flag distingue "aucun joueur pour cette saison" de "joueurs présents mais sans
  // simulation détectée sur la saison", pour guider le coach vers la bonne saison.
  const hasAnySeasonSimulation = useMemo(
    () => playersForOverview.some(hasOverviewSimulation),
    [playersForOverview]
  );
  const isCurrentSeasonSelected = season === getCurrentSeasonLabel();
  const [lastDate, setLastDate] = useState(null);
  const [currentTab, setCurrentTab] = useState('loads');
  const [loadTab, setLoadTab] = useState('internal');
  const [feedbackTab, setFeedbackTab] = useState("subjective");
  const [riskMode, setRiskMode] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_TEAM_OVERVIEW_RISK_MODE;
    return readTeamOverviewRiskMode(window.sessionStorage);
  });
  const [activeAlertPlayerIds, setActiveAlertPlayerIds] = useState(() => new Set());
  const isAtRiskMode = riskMode === RISK_MODE_AT_RISK;
  const navigate = useNavigate();
  const navigateToPlayerDetails = useCallback((playerOrId, source = "overview") => {
    const playerId = typeof playerOrId === "object" ? (playerOrId?.id ?? playerOrId?.player_id) : playerOrId;
    if (!playerId) {
      console.warn("[TeamOverview] NAVIGATE BLOCKED", { source, playerOrId });
      return;
    }

    const encodedId = encodeId(playerId);
    const to = `/players/${encodedId}`;
    prefetchPlayerDetailsData(playerId, season);
    prefetchPlayerDetailsPage(() => import("../../pages/PlayerDetailsPage"));
    const payload = {
      source,
      playerId,
      encodedId,
      to,
      season,
      currentUrl: window.location.href,
    };
    window.__PT_LAST_PLAYER_NAV = payload;
    // console.log("[TeamOverview] NAVIGATE REQUEST", payload);
    navigate(to, { state: { from: "TeamOverview", source, playerId, season } });
  }, [navigate, season]);
  const [rpeData, setRpeData] = useState([]);
  const [templateMeta, setTemplateMeta] = useState({ id: null, code: null });
  const [hooperTemplateName, setHooperTemplateName] = useState(null);
  const [subjectiveLoading, setSubjectiveLoading] = useState(false);
  const [subjectiveError, setSubjectiveError] = useState(null);
  const [subjectiveViewMode, setSubjectiveViewMode] = useState(FEEDBACK_VIEW_OVERVIEW);
  const [rpeViewMode, setRpeViewMode] = useState(FEEDBACK_VIEW_OVERVIEW);
  const [subjectiveHistory, setSubjectiveHistory] = useState([]);
  const [rpeHistory, setRpeHistory] = useState([]);
  const [subjectiveHistoryLoading, setSubjectiveHistoryLoading] = useState(false);
  const [rpeHistoryLoading, setRpeHistoryLoading] = useState(false);
  const [hooperReminderLoading, setHooperReminderLoading] = useState(false);
  const [hooperReminderMessage, setHooperReminderMessage] = useState(null);
  const [hooperReminderError, setHooperReminderError] = useState(null);

  const MAIN_TABS = {
    loads:     { label: t('tabs.loads'),     Icon: Activity,  gradient: "bg-gradient-to-r from-indigo-600 to-violet-600" },
    feedbacks: { label: t('tabs.feedbacks'), Icon: BarChart3, gradient: "bg-gradient-to-r from-pink-600 to-fuchsia-600" },
    // Global Stats hidden for now (national teams only).
    // ...(isNationalTeam ? {
    //   global: { label: t('tabs.global_stats'), Icon: Trophy,  gradient: "bg-gradient-to-r from-amber-500 to-orange-600" },
    // } : {}),
  };

  const LOAD_TABS = {
    internal: { label: t('tabs.loads_internal'), Icon: Brain,    gradient: "bg-gradient-to-r from-indigo-600 to-violet-600" },
    external: { label: t('tabs.loads_external'), Icon: Activity, gradient: "bg-gradient-to-r from-emerald-600 to-teal-600" },
  };

  const FEEDBACK_TABS = {
    subjective: { label: t('tabs.subjective'), Icon: ClipboardList, gradient: "bg-gradient-to-r from-sky-600 to-cyan-600" },
    rpe:        { label: t('tabs.rpe'),        Icon: Gauge,         gradient: "bg-gradient-to-r from-amber-600 to-orange-600" },
  };

  // Date navigation for Hooper and RPE tabs
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const todayRpeSportDay = () => currentRpeSportDayIso();
  const [hooperDate, setHooperDate] = useState(todayISO);
  const [rpeDate, setRpeDate] = useState(todayRpeSportDay);
  const [hooperSessions, setHooperSessions] = useState([]);
  const [selectedHooperSessionKey, setSelectedHooperSessionKey] = useState(null);
  const [rpeSessions, setRpeSessions] = useState([]);
  const [dayCatalogRpeSessions, setDayCatalogRpeSessions] = useState([]);
  const [selectedRpeSessionId, setSelectedRpeSessionId] = useState(null);
  const [isRpeModalOpen, setRpeModalOpen] = useState(false);
  const [isRpeConfirmOpen, setIsRpeConfirmOpen] = useState(false);
  const [rpeModalMode, setRpeModalMode] = useState("live");
  const [rpeRefreshNonce, setRpeRefreshNonce] = useState(0);
  const rpeFetchSeqRef = useRef(0);
  const rpeDateChangeRef = useRef(true);
  const orderedRpeSessions = useMemo(
    () => sortRpeSessionsChronologically(rpeSessions),
    [rpeSessions]
  );
  const mergedRpeSessions = useMemo(
    () => mergeRpeSessionsForDay(orderedRpeSessions, dayCatalogRpeSessions, rpeDate),
    [orderedRpeSessions, dayCatalogRpeSessions, rpeDate]
  );

  const k = (name) => `${STORAGE_PREFIX}${name}`;
  const club_id = me?.club_id || localStorage.getItem(k("club_id")) || localStorage.getItem(k("user_id"));

  const shiftDate = (isoDate, deltaDays) => {
    const d = new Date(isoDate);
    d.setDate(d.getDate() + deltaDays);
    return d.toISOString().slice(0, 10);
  };

  const isToday = (isoDate) => isoDate === todayISO();
  const isRpeSportDayToday = (isoDate) => isoDate === todayRpeSportDay();

  const formatNavDate = (isoDate) => {
    const d = new Date(isoDate + "T12:00:00");
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
  };

  const selectedHooperSession = hooperSessions.find((s) => s.session_key === selectedHooperSessionKey) || null;
  const selectedRpeSession =
    mergedRpeSessions.find(
      (session) => normalizeRpeSessionId(session.session_id) === normalizeRpeSessionId(selectedRpeSessionId)
    ) || null;
  const rpeSessionTargetIds = useMemo(() => {
    const targets = selectedRpeSession?.target_player_ids;
    if (!Array.isArray(targets) || targets.length === 0) return null;
    return new Set(targets.map((id) => String(id)));
  }, [selectedRpeSession?.target_player_ids, selectedRpeSession?.session_id]);
  const rpeOverviewPlayers = useMemo(() => {
    let list = playersForOverview;
    if (rpeSessionTargetIds) {
      list = list.filter((player) => rpeSessionTargetIds.has(String(player.id)));
    }
    if (selectedRpeSessionId && Array.isArray(rpeData) && rpeData.length > 0) {
      const responderIds = new Set(
        rpeData
          .filter((row) => row?.rpe_value !== "N/A" && row?.rpe_value != null)
          .map((row) => String(row.player_id))
      );
      if (responderIds.size > 0) {
        const responders = playersForOverview.filter((player) => responderIds.has(String(player.id)));
        const merged = new Map(list.map((player) => [String(player.id), player]));
        responders.forEach((player) => merged.set(String(player.id), player));
        list = [...merged.values()];
      }
    }
    if (selectedPosition === "All") return list;
    return list.filter((player) => player.position === selectedPosition);
  }, [playersForOverview, selectedPosition, rpeSessionTargetIds, selectedRpeSessionId, rpeData]);
  const selectedHooperTemplateId = selectedHooperSession?.survey_id || responses?.[0]?.template_id || null;

  const formatSessionTime = (iso) => {
    if (!iso) return "--:--";
    return formatLocalTime(iso, { locale: "fr-FR" }) || "--:--";
  };
  const formatSessionDateTime = (iso) => {
    if (!iso) return "--";
    return formatLocalDateTime(iso, { locale: "fr-FR", dateStyle: "short", timeStyle: "short" }) || "--";
  };

  const formatHooperSessionLabel = (session, index) => {
    const prefix =
      session.kind === "rpe_linked"
        ? "RPE"
        : session.period === "morning"
          ? "Morning"
          : session.period === "afternoon"
            ? "Afternoon"
            : `Session ${index + 1}`;
    const count = Number.isFinite(session.response_count) ? `${session.response_count} rep.` : null;
    return [prefix, formatSessionTime(session.submitted_at || session.created_at), count].filter(Boolean).join(" - ");
  };

  const handleHooperReminder = useCallback(async () => {
    setHooperReminderLoading(true);
    setHooperReminderError(null);
    setHooperReminderMessage(null);

    try {
      const payload = {
        date: hooperDate,
        ...(selectedHooperSessionKey ? { session_key: selectedHooperSessionKey } : {}),
        lang: i18n?.language?.startsWith("fr") ? "fr" : "en",
      };
      const response = await fetchAuth("/surveys/hooper/remind", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || data?.error || "Unable to send reminder");
      }
      if (data?.sent > 0) {
        setHooperReminderMessage(t("feedback.hooper_remind_success", {
          count: data.sent,
          defaultValue: "{{count}} reminder(s) sent.",
        }));
      } else {
        setHooperReminderMessage(t("feedback.hooper_remind_none", {
          defaultValue: "No reminder needed for this Hooper.",
        }));
      }
    } catch (error) {
      setHooperReminderError(error?.message || t("feedback.hooper_remind_error", { defaultValue: "Unable to send reminder." }));
    } finally {
      setHooperReminderLoading(false);
    }
  }, [hooperDate, selectedHooperSessionKey, t, i18n?.language]);

  const formatRpeSessionLabel = (session, index) => {
    const isOpen = session.is_open || !session.closed_at;
    const status = isOpen
      ? t("rpe.session_open", { defaultValue: "Open" })
      : t("rpe.session_closed", { defaultValue: "Closed" });
    const count = Number.isFinite(session.responses_count) ? `${session.responses_count} rep.` : null;
    const labelPrefix = session.label ? `${session.label}` : `Session ${index + 1}`;
    return [labelPrefix, formatSessionDateTime(session.created_at), count, status]
      .filter(Boolean)
      .join(" - ");
  };

  const handleRpeSessionChange = useCallback((rawSessionId) => {
    const normalized = normalizeRpeSessionId(rawSessionId) || null;
    setSelectedRpeSessionId(normalized);
    setRpeViewMode(FEEDBACK_VIEW_OVERVIEW);
  }, []);

  const handleRpeSessionMutated = useCallback((payload = {}) => {
    if (payload.type === "deleted" && payload.sessionId) {
      setSelectedRpeSessionId((current) =>
        normalizeRpeSessionId(current) === normalizeRpeSessionId(payload.sessionId) ? null : current
      );
    }
    setRpeRefreshNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    setHooperReminderMessage(null);
    setHooperReminderError(null);
  }, [hooperDate, selectedHooperSessionKey]);

  useEffect(() => {
    if (!selectedRpeSessionId) return;
    const stillValid = mergedRpeSessions.some(
      (session) => normalizeRpeSessionId(session.session_id) === normalizeRpeSessionId(selectedRpeSessionId)
    );
    if (!stillValid) {
      setSelectedRpeSessionId(null);
      setRpeViewMode(FEEDBACK_VIEW_OVERVIEW);
    }
  }, [mergedRpeSessions, selectedRpeSessionId]);

  const hydrateHooperTemplate = useCallback(async (templateId) => {
    if (!templateId) {
      setQuestions([]);
      setColumnTitles([]);
      setTemplateMeta({ id: null, code: null });
      setHooperTemplateName(null);
      return;
    }
    try {
      const response = await fetchAuth(`/surveys/${encodeId(templateId)}`);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data = await response.json();
      const activeQuestions = (data.questions || []).filter((q) => q.active !== false);
      const nextTitles = activeQuestions.map((question) => {
        const tagCode = question?.tag?.code;
        return tagCode && tagCode !== "unknown" ? tagCode : question.type;
      });
      setQuestions(activeQuestions);
      setColumnTitles(nextTitles);
      setTemplateMeta({ id: data.id, code: data.code });
      setHooperTemplateName(data.name || null);
    } catch {
      setQuestions([]);
      setColumnTitles([]);
      setTemplateMeta({ id: null, code: null });
      setHooperTemplateName(null);
    }
  }, []);

  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(null);

  const [internalIntensityCollapsed, setInternalIntensityCollapsed] = useState(false);
  const [internalMechanicalCollapsed, setInternalMechanicalCollapsed] = useState(false);

  const [externalAccelCollapsed, setExternalAccelCollapsed] = useState(false);
  const [externalCodCollapsed, setExternalCodCollapsed] = useState(false);
  const [externalViewMode, setExternalViewMode] = useState("histogram"); // "table" | "histogram"
  const [internalViewMode, setInternalViewMode] = useState("table"); // "table" | "histogram"

  // Shared metric visibility (synced between histogram & table views)
  const [internalVisibleMetrics, setInternalVisibleMetrics] = useState(() => {
    try {
      const saved = sessionStorage.getItem("internalLoad_visibleMetrics");
      if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed)) return new Set(parsed); }
    } catch {} return new Set(["trainability_index","mechanical_fatigue","intensity_index","RII","mechanical_load_index","MLI"]);
  });
  const [externalVisibleMetrics, setExternalVisibleMetrics] = useState(() => {
    try {
      const saved = sessionStorage.getItem("externalLoad_visibleMetrics");
      if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed)) return new Set(parsed); }
    } catch {} return new Set(["global_distance","hsr_distance_m","spr_distance_m","sprint_max","sprint_count","COD_number","acceleration_max","deceleration_max","accel_count","decel_count"]);
  });
  const effectiveInternalVisibleMetrics = hasLimitedClubUserAccess
    ? LIMITED_INTERNAL_METRIC_SET
    : internalVisibleMetrics;

  // Metric descriptors for the sidebar (table mode)
  const INTERNAL_METRIC_ITEMS = [
    { key: "trainability_index", label: "Trainability", color: getTrainabilityLevelColor("optimal"), icon: Brain },
    { key: "mechanical_fatigue", label: "Mechanical Fatigue", color: "#f59e0b", icon: AlertTriangle },
    { key: "intensity_index", label: "Intensity", color: "#6366f1", icon: Activity },
    { key: "RII", label: "RII", color: "#8b5cf6", icon: Flame },
    { key: "mechanical_load_index", label: "Mechanical Load", color: "#64748b", icon: Gauge },
    { key: "MLI", label: "MLI", color: "#10b981", icon: BarChart3 },
  ];
  const EXTERNAL_METRIC_ITEMS = [
    { key: "global_distance", label: "Total Distance", color: "#64748b", icon: Gauge },
    { key: "hsr_distance_m", label: "HSR", color: "#10b981", icon: Activity },
    { key: "spr_distance_m", label: "SPR", color: "#f43f5e", icon: Flame },
    { key: "sprint_max", label: "Max Sprint Speed", color: "#059669", icon: Activity },
    { key: "sprint_count", label: "Sprints", color: "#8b5cf6", icon: Flame },
    { key: "COD_number", label: "COD", color: "#a78bfa", icon: BarChart3 },
    { key: "acceleration_max", label: "Max Accel", color: "#6366f1", icon: Gauge },
    { key: "deceleration_max", label: "Max Decel", color: "#f59e0b", icon: AlertTriangle },
    { key: "accel_count", label: "Accel Count", color: "#818cf8", icon: Gauge },
    { key: "decel_count", label: "Decel Count", color: "#fbbf24", icon: AlertTriangle },
  ];

  const hasPlayers =
    (Array.isArray(players) && players.length > 0) ||
    (Array.isArray(allPlayers) && allPlayers.length > 0);
  const isSoftLoading = loading && hasPlayers;
  const showMainLoader = loading && !hasPlayers;

  const LoadingIndicator = ({ label, compact = false }) => (
    <div className={`flex items-center justify-center ${compact ? "py-3" : "py-8"}`} role="status" aria-live="polite">
      <span
        className={`inline-block h-5 w-5 mr-2 rounded-full border-2 border-current border-t-transparent animate-spin ${
          isDarkMode ? "text-indigo-300" : "text-indigo-600"
        }`}
      />
      <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>{label ?? t('common.loading')}</span>
    </div>
  );

  useEffect(() => {
    hydrateHooperTemplate(selectedHooperTemplateId);
  }, [selectedHooperTemplateId, hydrateHooperTemplate]);

  useEffect(() => {
    if (!hasLimitedClubUserAccess) return;
    setSubjectiveViewMode(FEEDBACK_VIEW_OVERVIEW);
    setRpeViewMode(FEEDBACK_VIEW_OVERVIEW);
  }, [hasLimitedClubUserAccess]);

  useEffect(() => {
    const fetchResponses = async () => {
      setSubjectiveLoading(true);
      setSubjectiveError(null);
      try {
        const params = new URLSearchParams({
          date: hooperDate,
          include_sessions: "1",
        });
        if (selectedHooperSessionKey) {
          params.set("session_key", selectedHooperSessionKey);
        }
        const url = `/clubs/surveys/response/latest?${params.toString()}`;
        const res = await fetchAuth(url);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = await res.json();
        let nextSelectedSessionKey = null;
        let nextSessions = [];
        if (Array.isArray(data)) {
          const dayResponses = keepLatestByPlayer(data, {
            targetDate: hooperDate,
            dateKeys: ["submitted_at"],
            timeKeys: ["submitted_at"],
          });
          setResponses(dayResponses);
          setHooperSessions([]);
          setSelectedHooperSessionKey(null);
          nextSelectedSessionKey = null;
          nextSessions = [];
        } else {
          const sessions = Array.isArray(data.sessions) ? data.sessions : [];
          const selectedKey = data.selected_session_key || sessions[0]?.session_key || null;
          const dayResponses = keepLatestByPlayer(
            Array.isArray(data.responses) ? data.responses : [],
            {
              targetDate: hooperDate,
              dateKeys: ["submitted_at"],
              timeKeys: ["submitted_at"],
            }
          );
          setHooperSessions(sessions);
          setSelectedHooperSessionKey(selectedKey);
          setResponses(dayResponses);
          nextSelectedSessionKey = selectedKey;
          nextSessions = sessions;
        }

        const selectedMeta = nextSessions.find((s) => s.session_key === nextSelectedSessionKey) || null;
        if (selectedMeta?.kind === "rpe_linked") {
          setPreviousResponses([]);
        } else {
          const prevDate = shiftDate(hooperDate, -1);
          const prevParams = new URLSearchParams({
            date: prevDate,
            include_sessions: "1",
          });
          if (selectedMeta?.kind === "period" && selectedMeta?.period) {
            prevParams.set("session_key", `period:${selectedMeta.period}`);
          }
          const prevRes = await fetchAuth(`/clubs/surveys/response/latest?${prevParams.toString()}`);
          if (!prevRes.ok) throw new Error(`Request failed: ${prevRes.status}`);
          const prevData = await prevRes.json();
          const previousDayResponses = keepLatestByPlayer(
            Array.isArray(prevData) ? prevData : (prevData.responses ?? []),
            {
              targetDate: prevDate,
              dateKeys: ["submitted_at"],
              timeKeys: ["submitted_at"],
            }
          );
          setPreviousResponses(previousDayResponses);
        }
      } catch (err) {
        setSubjectiveError(err?.message || "Unable to load subjective sessions");
        setPreviousResponses([]);
      } finally {
        setSubjectiveLoading(false);
      }
    };

    fetchResponses();
  }, [hooperDate, selectedHooperSessionKey]);

  useEffect(() => {
    rpeDateChangeRef.current = true;
    setSelectedRpeSessionId(null);
    setRpeSessions([]);
    setRpeData([]);
    setRpeViewMode(FEEDBACK_VIEW_OVERVIEW);
  }, [rpeDate, season]);

  useEffect(() => {
    if (currentTab !== "feedbacks" || feedbackTab !== "rpe" || !club_id) return;

    let cancelled = false;
    const loadDayCatalogSessions = async () => {
      try {
        const params = new URLSearchParams({ date: rpeDate });
        const response = await fetchAuth(`/rpe_sessions/${club_id}/for_day?${params.toString()}`);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) {
          setDayCatalogRpeSessions(Array.isArray(data?.sessions) ? data.sessions : []);
        }
      } catch {
        if (!cancelled) setDayCatalogRpeSessions([]);
      }
    };

    loadDayCatalogSessions();
    return () => {
      cancelled = true;
    };
  }, [currentTab, feedbackTab, club_id, rpeDate, isRpeModalOpen, rpeRefreshNonce]);

  useEffect(() => {
    let cancelled = false;
    const fetchSeq = ++rpeFetchSeqRef.current;
    const sessionForFetch = rpeDateChangeRef.current ? null : selectedRpeSessionId;
    rpeDateChangeRef.current = false;

    const fetchRpeData = async () => {
      try {
        const params = new URLSearchParams({
          date: rpeDate,
          include_sessions: "1",
        });
        if (season) {
          params.set("season", season);
        }
        if (sessionForFetch) {
          params.set("session_id", sessionForFetch);
        }
        const url = `/clubs/latest_rpe?${params.toString()}`;
        const response = await fetchAuth(url);
        if (!response.ok) throw new Error(`Failed to fetch RPE data: ${response.status}`);
        const data = await response.json();
        if (cancelled || fetchSeq !== rpeFetchSeqRef.current) return;

        if (Array.isArray(data)) {
          const dayRows = keepLatestByPlayer(data, {
            timeKeys: ["date"],
          });
          setRpeSessions([]);
          setRpeData(dayRows);
          setSelectedRpeSessionId(null);
          return;
        }

        const sessions = sortRpeSessionsChronologically(
          Array.isArray(data.sessions) ? data.sessions : []
        );
        const dayRows = keepLatestByPlayer(
          Array.isArray(data.rows) ? data.rows : [],
          {
            timeKeys: ["date"],
          }
        );

        setRpeSessions(sessions);
        setRpeData(dayRows);
        setSelectedRpeSessionId(
          resolveRpeSessionSelection(sessionForFetch, sessions, data.selected_session_id)
        );
      } catch (error) {}
    };
    fetchRpeData();

    return () => {
      cancelled = true;
    };
  }, [rpeDate, season, selectedRpeSessionId, rpeRefreshNonce]);

  useEffect(() => {
    if (currentTab !== "feedbacks" || feedbackTab !== "subjective") return;

    let cancelled = false;

    const loadSubjectiveHistory = async () => {
      setSubjectiveHistoryLoading(true);
      try {
        const dates = Array.from({ length: FEEDBACK_HISTORY_DAYS }, (_, index) =>
          shiftDate(hooperDate, index - (FEEDBACK_HISTORY_DAYS - 1))
        );
        const periodScope = selectedHooperSession?.kind === "period" ? selectedHooperSession.period : null;

        const dayEntries = await Promise.all(
          dates.map(async (dateIso) => {
            try {
              const params = new URLSearchParams({ date: dateIso, include_sessions: "1" });
              if (periodScope) {
                params.set("session_key", `period:${periodScope}`);
              }
              const response = await fetchAuth(`/clubs/surveys/response/latest?${params.toString()}`);
              if (!response.ok) return { date: dateIso, responses: [] };
              const data = await response.json();
              const dayResponses = keepLatestByPlayer(
                Array.isArray(data)
                  ? data
                  : Array.isArray(data?.responses)
                    ? data.responses
                    : [],
                {
                  targetDate: dateIso,
                  dateKeys: ["submitted_at"],
                  timeKeys: ["submitted_at"],
                }
              );
              return { date: dateIso, responses: dayResponses };
            } catch {
              return { date: dateIso, responses: [] };
            }
          })
        );

        if (!cancelled) {
          setSubjectiveHistory(dayEntries);
        }
      } finally {
        if (!cancelled) {
          setSubjectiveHistoryLoading(false);
        }
      }
    };

    loadSubjectiveHistory();

    return () => {
      cancelled = true;
    };
  }, [currentTab, feedbackTab, hooperDate, selectedHooperSession?.kind, selectedHooperSession?.period]);

  useEffect(() => {
    if (currentTab !== "feedbacks" || feedbackTab !== "rpe") return;

    let cancelled = false;

    const loadRpeHistory = async () => {
      setRpeHistoryLoading(true);
      try {
        const dates = Array.from({ length: RPE_TREND_HISTORY_DAYS }, (_, index) =>
          shiftDate(rpeDate, index - (RPE_TREND_HISTORY_DAYS - 1))
        );

        const dayEntries = await Promise.all(
          dates.map(async (dateIso) => {
            try {
              const params = new URLSearchParams({ date: dateIso, for_trend: "1" });
              if (season) {
                params.set("season", season);
              }
              const response = await fetchAuth(`/clubs/latest_rpe?${params.toString()}`);
              if (!response.ok) return { date: dateIso, sessions: [], rows: [] };
              const data = await response.json();
              const sessionTrends = Array.isArray(data?.session_trends) ? data.session_trends : [];
              const dayRows = keepLatestByPlayer(
                Array.isArray(data?.rows) ? data.rows : [],
                {
                  timeKeys: ["date"],
                }
              );
              return {
                date: dateIso,
                sessions: sessionTrends,
                rows: dayRows,
                coachRpeValue: sessionTrends[0]?.coach_rpe_value ?? null,
              };
            } catch {
              return { date: dateIso, sessions: [], rows: [] };
            }
          })
        );

        if (!cancelled) {
          setRpeHistory(dayEntries);
        }
      } finally {
        if (!cancelled) {
          setRpeHistoryLoading(false);
        }
      }
    };

    loadRpeHistory();

    return () => {
      cancelled = true;
    };
  }, [currentTab, feedbackTab, rpeDate, season, rpeRefreshNonce]);

  useEffect(() => {
    let cancelled = false;
    async function loadAvailability() {
      setAvailabilityLoading(true);
      try {
        setAvailabilityError(null);
        const res = await fetchAuth(`/absences/availability`);
        if (!res.ok) throw new Error(`Availability failed: ${res.status}`);
        const json = await res.json();
        if (!cancelled) setAvailability(json);
      } catch (e) {
        if (!cancelled) setAvailabilityError(e);
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    }
    loadAvailability();
    const id = setInterval(loadAvailability, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [season]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(TEAM_OVERVIEW_RISK_MODE_STORAGE_KEY, riskMode);
    } catch {}
  }, [riskMode]);

  useEffect(() => {
    let cancelled = false;

    const loadActiveAlerts = async () => {
      try {
        const { alerts } = await fetchAlerts({ status: "all", limit: 200, offset: 0 });
        if (!cancelled) {
          setActiveAlertPlayerIds(buildActiveAlertPlayerIdSet(alerts));
        }
      } catch {
        // Keep current alert set when alerts endpoint is temporarily unavailable.
      }
    };

    loadActiveAlerts();
    const intervalId = window.setInterval(loadActiveAlerts, ALERTS_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(playersForOverview) || playersForOverview.length === 0) {
      setFilteredPlayers([]);
      setDisplayedPlayers([]);
      return;
    }

    const playersWithSimulation = playersForOverview.filter(hasOverviewSimulation);
    if (playersWithSimulation.length === 0) {
      setFilteredPlayers([]);
      setDisplayedPlayers([]);
      return;
    }

    const nextPlayers =
      riskMode === RISK_MODE_AT_RISK
        ? playersWithSimulation.filter((player) =>
            isPlayerAtRisk(player, {
              activeAlertPlayerIds,
              trainabilityThreshold: AT_RISK_TRAINABILITY_THRESHOLD,
            })
          )
        : playersWithSimulation;

    setFilteredPlayers(nextPlayers);
  }, [playersForOverview, riskMode, activeAlertPlayerIds]);

  useEffect(() => {
    if (loading) {
      setLastDate(null);
      return;
    }
    if (!mostRecentSimulation || mostRecentSimulation === "N/A") {
      setLastDate(null);
      return;
    }
    setLastDate(mostRecentSimulation);
  }, [mostRecentSimulation, loading, season]);

  const getFlagColor = (value, type) => {
    if (value === null || value === undefined || isNaN(value)) return "";
    switch (type) {
      case "intensity_index":
        if (value > 7) return "text-red-600 font-bold";
        if (value > 4.4) return "text-orange-500 font-bold";
        if (value < 1) return "text-blue-600 font-bold";
        break;
      case "mechanical_load_index":
        if (value > 7) return "text-red-600 font-bold";
        if (value > 4.4) return "text-orange-500 font-bold";
        if (value < 1) return "text-blue-600 font-bold";
        break;
      case "fatigue":
        if (value >= 70) return "text-red-600 font-bold";
        if (value >= 48) return "text-orange-500 font-bold";
        break;
      case "MLI":
      case "RII":
        if (value < 70) return "text-orange-500 font-bold";
        break;
    }
    return isDarkMode ? "text-white" : "text-black";
  };

  const getRiskColor = (fatigue) => {
    if (fatigue >= 70) return "text-red-600 font-bold";
    if (fatigue >= 48) return "text-orange-500 font-bold";
    return `${isDarkMode ? "text-white" : "text-black"}`;
  };

  const clamp01 = (x) => Math.max(0, Math.min(1, x));

  const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const n = parseInt(full, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };

  const rgbToHex = ({ r, g, b }) => {
    const to2 = (v) => v.toString(16).padStart(2, "0");
    return `#${to2(r)}${to2(g)}${to2(b)}`;
  };

  const lerpColor = (aHex, bHex, t) => {
    const a = hexToRgb(aHex);
    const b = hexToRgb(bHex);
    const tt = clamp01(t);
    return rgbToHex({
      r: Math.round(a.r + (b.r - a.r) * tt),
      g: Math.round(a.g + (b.g - a.g) * tt),
      b: Math.round(a.b + (b.b - a.b) * tt),
    });
  };

  const rgba = (hex, alpha = 0.12) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const trainabilityColor = (score10, status) =>
    getTrainabilityDisplayColor({ score: score10, status });

  const getTrainabilityLevel = (trainabilityLevelObj, group, side) =>
    resolveTrainabilityLevel(trainabilityLevelObj, { group, side });

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const toTrainabilityScore10 = getTrainabilityScore10;

  const trainDelta10 = (currRaw, prevRaw) =>
    safeDelta(toTrainabilityScore10(currRaw), toTrainabilityScore10(prevRaw));

  const formatTrainability = (raw) =>
    formatTrainabilityScore(raw) ?? t("common.na");

  const ratioColor = (ratio) => {
    if (ratio >= 0.75) return "text-red-600 font-bold";
    if (ratio >= 0.5)  return "text-yellow-500 font-bold";
    return "text-green-600 font-bold";
  };

  const severityColor = (sev) => {
    if (sev >= 8) return "text-red-600 font-bold";
    if (sev >= 5) return "text-yellow-500 font-bold";
    return "text-green-600 font-bold";
  };

  function cellClass({ value, max, type }) {
    if (type !== "scale" || typeof value !== "number" || typeof max !== "number" || max <= 0)
      return "";
    return ratioColor(value / max);
  }

  useEffect(() => {
    if (selectedPosition === "All") {
      setDisplayedPlayers(filteredPlayers);
    } else {
      setDisplayedPlayers(filteredPlayers.filter((player) => player.position === selectedPosition));
    }
  }, [selectedPosition, filteredPlayers]);

  useEffect(() => {
    if (currentTab === "loads" && selectedPosition === "Goalkeeper") {
      setSelectedPosition("All");
    }
  }, [currentTab, selectedPosition]);

  const parseDate = (dateString) => {
    const parts = dateString.split("/");
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return new Date("1970-01-01");
  };

  const getTrainabilityFocusColor = (muscleTrainability, globalRaw, globalStatus) => {
    const style = getTrainabilityTextStyle({
      score: globalRaw,
      status: globalStatus,
      isDarkMode,
    });
    return style.className;
  };

  const detectMuscleScale = (muscleTrainability) => {
    const vals = [];
    if (!muscleTrainability) return "ratio";
    Object.values(muscleTrainability).forEach((sides) => {
      ["left", "right"].forEach((side) => {
        const v = Number(sides?.[side]);
        if (Number.isFinite(v)) vals.push(v);
      });
    });
    const maxV = vals.length ? Math.max(...vals) : 1;
    return maxV <= 1 ? "ratio" : "score10";
  };

  const formatTrainabilityFocus = (player) => {
    const global10 = toTrainabilityScore10(player?.trainability_index);
    const muscleTrainability = player?.muscle_trainability;
    const trainabilityLevelObj = player?.trainability_level;

    if (!muscleTrainability || !Number.isFinite(global10)) return t('common.na');
    if (global10 >= TRAINABILITY_TARGET_10) return null;

    const muscleScale = detectMuscleScale(muscleTrainability);
    const HIGH = muscleScale === "ratio" ? TRAINABILITY_TARGET_10 / 10 : TRAINABILITY_TARGET_10;

    const entries = [];
    Object.entries(muscleTrainability).forEach(([group, sides]) => {
      ["left", "right"].forEach((side) => {
        const v = Number(sides?.[side]);
        if (Number.isFinite(v)) entries.push({ group, side, value: v });
      });
    });

    const candidates = entries
      .filter((e) => e.value < HIGH)
      .sort((a, b) => a.value - b.value)
      .slice(0, 4);

    if (!candidates.length) return t('common.na');

    const baseCls = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border";

    return (
      <div className="flex flex-wrap gap-1">
        {candidates.map(({ group, side, value }) => {
          const sideLabel = side === "left" ? "L" : "R";
          const value10 = muscleScale === "ratio" ? value * 10 : value;
          const status = getTrainabilityLevel(trainabilityLevelObj, group, side);
          const col = trainabilityColor(value10, status);
          const textCol = status
            ? getTrainabilityLevelTextColor(status)
            : col;
          const displayVal =
            muscleScale === "ratio"
              ? `${(value * 100).toFixed(1)}%`
              : `${value.toFixed(1)}/10`;

          return (
            <span
              key={`${group}-${side}`}
              className={baseCls}
              style={
                textCol
                  ? {
                      color: textCol,
                      borderColor: rgba(textCol, 0.4),
                      fontWeight: 700,
                    }
                  : undefined
              }
            >
              <span>{group}</span>
              <span>({sideLabel})</span>
              <span className="tabular-nums">{displayVal}</span>
            </span>
          );
        })}
      </div>
    );
  };

  const renderDynamicColumnData = (questions, playerResponses, playerId, prevPlayerResponses) => {
    const playerResponse = playerResponses?.[0];
    const answers = playerResponse?.answers || [];
    const responseId = playerResponse?.id || null;

    const prevPlayerResponse = prevPlayerResponses?.[0];
    const prevAnswers = prevPlayerResponse?.answers || [];

    const handleAnswerSaved = (questionId, newValue) => {
      setResponses(prev => prev.map(r => {
        if (r.id !== responseId) return r;
        const shouldDelete = newValue === null || newValue === undefined;
        const exists = r.answers.some(a => a.question_id === questionId);
        return {
          ...r,
          answers: shouldDelete
            ? r.answers.filter(a => a.question_id !== questionId)
            : exists
              ? r.answers.map(a =>
                  a.question_id === questionId ? { ...a, answer: newValue } : a
                )
              : [...r.answers, { question_id: questionId, answer: newValue }],
        };
      }));
    };

    const handleResponseCreated = (newResponseId, questionId, newValue, sessionContext) => {
      setResponses(prev => {
        const idx = prev.findIndex(r => r.player_id === playerId);
        if (idx !== -1) {
          return prev.map((r, i) => {
            if (i !== idx) return r;
            const exists = r.answers.some(a => a.question_id === questionId);
            return {
              ...r,
              id: newResponseId,
              submitted_at: sessionContext?.submitted_at || new Date().toISOString(),
              session_key: sessionContext?.session_key || r.session_key,
              period: sessionContext?.period || r.period || null,
              linked_rpe_session_id: sessionContext?.linked_rpe_session_id || r.linked_rpe_session_id || null,
              answers: exists
                ? r.answers.map(a =>
                    a.question_id === questionId ? { ...a, answer: newValue } : a
                  )
                : [...r.answers, { question_id: questionId, answer: newValue }],
            };
          });
        }
        return [
          ...prev,
          {
            id: newResponseId,
            template_id: templateMeta.id,
            player_id: playerId,
            submitted_at: sessionContext?.submitted_at || new Date().toISOString(),
            session_key: sessionContext?.session_key || null,
            period: sessionContext?.period || null,
            linked_rpe_session_id: sessionContext?.linked_rpe_session_id || null,
            answers: [{ question_id: questionId, answer: newValue }],
          },
        ];
      });
    };

    return questions.map((question) => {
      const ans = answers.find(a => a.question_id === question.id) ?? {};
      const { answer, max, min, answer_label: label1, answerLabel: label2, options: ansOptions } = ans;

      const prevAns = prevAnswers.find(a => a.question_id === question.id);
      const prevValue = prevAns?.answer ?? null;

      const choiceFallbackLabels = Array.isArray(label1)
        ? label1
        : Array.isArray(label2)
          ? label2
          : [label1 ?? label2].filter(Boolean);

      const questionForCell = {
        id: question.id,
        type: question.type,
        config: {
          ...question.config,
          ...(max !== undefined ? { max } : {}),
          ...(min !== undefined ? { min } : {}),
        },
        options: normalizeChoiceOptions(question.options || ansOptions || []),
        choiceFallbackLabels,
      };

      let displayedValue = answer;
      if (question.type === "yesno" && typeof answer === "boolean") {
        displayedValue = answer;
      }

      const tdCls = `px-4 py-3 text-center ${cellClass({ value: answer, max, type: question.type })}`;

      return (
        <EditableCell
          key={question.id}
          value={displayedValue}
          previousValue={prevValue}
          question={questionForCell}
          responseId={responseId}
          playerId={playerId}
          templateId={templateMeta.id}
          isDark={isDarkMode}
          cellClassName={tdCls}
          onSaved={handleAnswerSaved}
          onResponseCreated={handleResponseCreated}
          targetDate={hooperDate}
          sessionContext={selectedHooperSession}
        />
      );
    });
  };

  const hasHooperAnswer = (playerId) => {
    const playerResponse = responses.find(
      (response) => String(response.player_id) === String(playerId)
    );
    return hasSubjectiveResponseAnswer(playerResponse);
  };

  const getRPEColor = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "text-gray-500";
    if (value >= 10) return "text-red-600 font-bold";
    if (value >= 5) return "text-yellow-500 font-bold";
    return "text-green-600 font-bold";
  };

  const InternalLoadCard = ({ player, isDarkMode, onClick }) => {
    const status = getPlayerAlertStatus(player);
    const borderColor =
      status === "high" ? "border-red-500" :
      status === "medium" ? "border-amber-400" : "border-emerald-500";

    return (
      <div
        onClick={onClick}
        className={`cursor-pointer rounded-2xl border-l-4 ${borderColor} p-4 shadow-sm transition 
          hover:-translate-y-0.5 hover:shadow-md ${
            isDarkMode ? "bg-gray-800/80 hover:bg-gray-800" : "bg-white hover:bg-gray-50"
          }`}
      >
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="font-semibold">{player.first_name} {player.last_name}</div>
            <div className="mt-1 inline-flex items-center rounded-full bg-gray-900/5 px-2 py-0.5 text-xs opacity-80">
              {player.position || t('common.na')}
            </div>
          </div>
          <div className="text-xs text-gray-400">
            {t('metrics.last_sim')}: {player.timeinfo_date || t('common.na')}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4 text-sm">
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide opacity-70">{t('metrics.intensity')} (PT)</div>
            <div className="flex flex-col items-start gap-1">
              <span className={getFlagColor(player.intensity_index, "intensity_index")}>
                {formatPT(player.intensity_index)}
              </span>
              <DeltaChip delta={safeDelta(player.intensity_index, player.intensity_index_prev)} unit=" PT" decimals={2} label={t('metrics.last_session')} />
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide opacity-70">{t('metrics.mechanical_fatigue')}</div>
            <div className="flex flex-col items-start gap-1">
              <span className={getFlagColor(player.mechanical_fatigue * 100, "fatigue")}>
                {formatPercentage(player.mechanical_fatigue)}
              </span>
              <DeltaChip delta={safeDelta(player.mechanical_fatigue, player.mechanical_fatigue_prev)} isPercent decimals={1} label={t('metrics.last_session')} invertTrendColors />
            </div>
          </div>

          {hasTrainability(player.trainability_index) && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide opacity-70">
                {t("metrics.trainability")}
              </div>

              {(() => {
                const score10 = toTrainabilityScore10(player.trainability_index);
                const status = getTrainabilityLevel(player.trainability_level, "global");
                const textStyle = getTrainabilityTextStyle({
                  score: score10,
                  status,
                  isDarkMode,
                });

                return (
                  <span
                    style={textStyle.color ? { color: textStyle.color } : undefined}
                    className={textStyle.className}
                  >
                    {formatTrainability(player.trainability_index)}
                  </span>
                );
              })()}
            </div>
          )}

          <div className="space-y-1 col-span-2 md:col-span-1">
            <div className="text-[11px] uppercase tracking-wide opacity-70">{t('metrics.muscle_fatigue_focus')}</div>
            <span className={getRiskColor(typeof player.at_risk_muscle_fatigue === "number" ? player.at_risk_muscle_fatigue * 100 : NaN)}>
              {player.at_risk_muscle_group && player.at_risk_muscle_group !== "None"
                ? `${player.at_risk_muscle_group} - ${player.at_risk_muscle_side || t('common.na')} ${typeof player.at_risk_muscle_fatigue === "number" ? formatPercentage(player.at_risk_muscle_fatigue) : t('common.na')}`
                : t('metrics.no_critical_muscle')}
            </span>
          </div>

          {hasTrainability(player.trainability_index) &&
            toTrainabilityScore10(player.trainability_index) < TRAINABILITY_TARGET_10 && (
              <div className="space-y-1 col-span-2">
                <div className="text-[11px] uppercase tracking-wide opacity-70">
                  {t("metrics.trainability_focus")}
                </div>

                <div
                  className={getTrainabilityFocusColor(
                    player.muscle_trainability,
                    player.trainability_index,
                    getTrainabilityLevel(player.trainability_level, "global")
                  )}
                >
                  {formatTrainabilityFocus(player)}
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  const getPlayerAlertStatus = (player) => {
    const toPct = (v) => (Number.isFinite(v) ? v * 100 : NaN);
    const fatiguePct = toPct(Number(player.mechanical_fatigue));
    const atRiskPct = toPct(Number(player.at_risk_muscle_fatigue));
    const riiPct = toPct(Number(player.RII));
    const mliPct = toPct(Number(player.MLI));
    const train = Number(player.trainability_index);
    const intensity = Number(player.intensity_index);
    const mech = Number(player.mechanical_load_index);

    if (fatiguePct >= 70 || atRiskPct >= 70 || train < TRAIN_LOW_10 || intensity > 7 || mech > 7) return "high";
    if (fatiguePct >= 48 || atRiskPct >= 48 || train < TRAIN_HIGH_10 || intensity > 4.4 || mech > 4.4 || riiPct < 70 || mliPct < 70) return "medium";
    return "low";
  };

  const RiskModeToggle = () => (
    <div
      className={`inline-flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
        isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-white"
      }`}
    >
      <span className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
        {t("team_overview.players_scope", { defaultValue: "Players" })}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={isAtRiskMode}
        onClick={() => setRiskMode(isAtRiskMode ? RISK_MODE_GLOBAL : RISK_MODE_AT_RISK)}
        title={isAtRiskMode
          ? t("team_overview.risk_mode_at_risk", { defaultValue: "At-risk" })
          : t("team_overview.risk_mode_global", { defaultValue: "Global" })}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          isAtRiskMode ? "bg-rose-500" : isDarkMode ? "bg-gray-600" : "bg-gray-400"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
            isAtRiskMode ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
      <span className={`text-xs font-medium ${isAtRiskMode ? isDarkMode ? "text-rose-300" : "text-rose-700" : isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
        {isAtRiskMode
          ? t("team_overview.risk_mode_at_risk", { defaultValue: "At-risk" })
          : t("team_overview.risk_mode_global", { defaultValue: "Global" })}
      </span>
    </div>
  );

  const PositionSelect = ({ includeGoalkeeper = true }) => {
    const positionOptions = includeGoalkeeper
      ? PLAYER_POSITIONS
      : PLAYER_POSITIONS.filter((position) => position.value !== "Goalkeeper");

    return (
      <select
        className="p-3 border rounded-lg bg-white text-black text-sm focus:ring focus:ring-blue-300 shadow-md"
        value={selectedPosition}
        onChange={(e) => setSelectedPosition(e.target.value)}
      >
        <option value="All">{t("positions.all")}</option>

        {positionOptions.map((position) => (
          <option key={position.value} value={position.value}>
            {t(position.labelKey)}
          </option>
        ))}
      </select>
    );
  };

  const displayedLoadPlayers = displayedPlayers.filter((player) =>
    hasLoadSimulationData(player)
  );

  const displayedPlayersWithTrainability = displayedLoadPlayers.filter((player) =>
    hasTrainability(player.trainability_index)
  );

  const hasInternalRestMetricsVisible =
    effectiveInternalVisibleMetrics.has("mechanical_fatigue") ||
    effectiveInternalVisibleMetrics.has("intensity_index") ||
    effectiveInternalVisibleMetrics.has("RII") ||
    effectiveInternalVisibleMetrics.has("mechanical_load_index") ||
    effectiveInternalVisibleMetrics.has("MLI");

  return (
    <div className={`p-6 min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}>
      <div className="mb-6 flex items-center justify-center">
          <h1 className="text-4xl font-bold text-center">{t('team_overview.title')}</h1>
      </div>

      {error && !hasPlayers && (
        <div className={`mb-4 rounded-xl border px-3 py-2 text-xs sm:text-sm ${isDarkMode ? "border-red-500/60 bg-red-500/10 text-red-200" : "border-red-400 bg-red-50 text-red-800"}`}>
          {t('team_overview.error_load')} <span className="font-medium">{t('team_overview.error_incomplete')}</span>
        </div>
      )}

      {isFallbackData && hasPlayers && (
        <div className={`mb-4 rounded-xl border px-3 py-2 text-xs sm:text-sm ${isDarkMode ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-amber-400 bg-amber-50 text-amber-800"}`}>
          {t('team_overview.degraded_mode')} <span className="font-medium">{t('team_overview.degraded_values')}</span>
        </div>
      )}

      {isSoftLoading && (
        <div className={`mb-4 rounded-xl border px-4 py-3 ${isDarkMode ? "border-indigo-500/40 bg-indigo-500/10" : "border-indigo-200 bg-indigo-50"}`}>
          <LoadingIndicator compact label={t('team_overview.refreshing')} />
        </div>
      )}

      {!loading && !error && hasPlayers && !hasAnySeasonSimulation && (
        <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${
          isDarkMode ? "border-sky-500/50 bg-sky-500/10 text-sky-100" : "border-sky-300 bg-sky-50 text-sky-900"
        }`}>
          <div>
            <p className="font-semibold">{t('team_overview.no_season_data_title', { season })}</p>
            <p className={`mt-0.5 text-xs ${isDarkMode ? "text-sky-200/80" : "text-sky-800/80"}`}>
              {t('team_overview.no_season_data_desc')}
            </p>
          </div>
          {!isCurrentSeasonSelected && (
            <button
              type="button"
              onClick={() => setSeason(getCurrentSeasonLabel())}
              className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                isDarkMode
                  ? "bg-sky-500/20 text-sky-100 hover:bg-sky-500/30"
                  : "bg-sky-600 text-white hover:bg-sky-700"
              }`}
            >
              {t('team_overview.back_to_current_season', { season: getCurrentSeasonLabel() })}
            </button>
          )}
        </div>
      )}

      {!loading && !error && !hasPlayers && (
        <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
          isDarkMode ? "border-gray-600 bg-gray-800/60 text-gray-200" : "border-gray-300 bg-gray-100 text-gray-700"
        }`}>
          <p className="font-semibold">{t('team_overview.no_players_title', { season })}</p>
          <p className={`mt-0.5 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {t('team_overview.no_players_desc')}
          </p>
        </div>
      )}

        <TeamOverviewMenu value={currentTab} onChange={setCurrentTab} isDark={isDarkMode} tabs={MAIN_TABS} />

      {(currentTab === "feedbacks" || currentTab === "loads") ? (
        <div className="mt-3 mb-4">
            <TeamOverviewMenu
              value={currentTab === "feedbacks" ? feedbackTab : loadTab}
              onChange={currentTab === "feedbacks" ? setFeedbackTab : setLoadTab}
              isDark={isDarkMode}
              tabs={currentTab === "feedbacks" ? FEEDBACK_TABS : LOAD_TABS}
            />
        </div>
      ) : (
        <div className="mt-6"></div>
      )}

      <div className="mb-4">
        {availabilityLoading && !availability ? (
          <div className={`rounded-2xl p-4 ring-1 ${isDarkMode ? "bg-gray-800/70 ring-white/10" : "bg-white ring-black/10"}`}>
            <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>{t('team_overview.loading_availability')}</div>
          </div>
        ) : availabilityError ? (
          <div className={`rounded-2xl p-4 ring-1 ${isDarkMode ? "bg-gray-800/70 ring-white/10" : "bg-white ring-black/10"}`}>
            <div className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>{t('team_overview.availability_unavailable')}</div>
          </div>
        ) : (
            <AvailabilityCompactCard data={availability} isDarkMode={isDarkMode} />
        )}
      </div>

      {/* ─── FEEDBACKS TAB ─── */}
      <div style={{ display: currentTab === "feedbacks" ? "block" : "none" }} className="space-y-6">
        {/* Hooper */}
        <div style={{ display: feedbackTab === "subjective" ? "block" : "none" }} className="space-y-6">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-3">
                <PositionSelect />
            </div>
            <div className="flex items-center gap-2">
                <RpeSportDayNavigator
                  value={hooperDate}
                  onChange={setHooperDate}
                  isDarkMode={isDarkMode}
                  todayValue={todayISO()}
                  maxDate={todayISO()}
                />

              {hooperSessions.length > 0 && (
                <div className={`inline-flex items-center gap-2 rounded-lg border px-2 py-1 ${isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-white"}`}>
                  <span className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>Session</span>
                  <select
                    value={selectedHooperSessionKey || ""}
                    onChange={(e) => setSelectedHooperSessionKey(e.target.value || null)}
                    className={`text-sm rounded px-2 py-1 border ${isDarkMode ? "bg-gray-900 border-gray-600 text-white" : "bg-white border-gray-300 text-black"}`}
                  >
                    {hooperSessions.map((session, index) => (
                      <option key={session.session_key} value={session.session_key}>
                        {formatHooperSessionLabel(session, index)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className={`-mt-1 flex flex-wrap items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
            {hooperTemplateName && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? "bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/30" : "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"}`}>
                Template: {hooperTemplateName}
              </span>
            )}
            {selectedHooperSession && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}>
                Session: {selectedHooperSession.kind === "period" ? selectedHooperSession.period : "RPE linked"}
              </span>
            )}
            {subjectiveError && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? "bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/30" : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"}`}>
                {subjectiveError}
              </span>
            )}
          </div>

          {!hasLimitedClubUserAccess && (
          <div className="flex justify-end">
            <div className={`inline-flex items-center rounded-lg border p-0.5 ${isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-100"}`}>
              <button
                type="button"
                onClick={() => setSubjectiveViewMode(FEEDBACK_VIEW_OVERVIEW)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  subjectiveViewMode === FEEDBACK_VIEW_OVERVIEW
                    ? isDarkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                    : isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("feedback.overview_mode", { defaultValue: "Overview" })}
              </button>
              <button
                type="button"
                onClick={() => setSubjectiveViewMode(FEEDBACK_VIEW_EDIT)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  subjectiveViewMode === FEEDBACK_VIEW_EDIT
                    ? isDarkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                    : isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("feedback.edit_entry", { defaultValue: "Modification de la saisie" })}
              </button>
            </div>
          </div>
          )}

          {(hasLimitedClubUserAccess || subjectiveViewMode === FEEDBACK_VIEW_OVERVIEW) ? (
            loading || subjectiveLoading ? (
              <LoadingIndicator />
            ) : (
              <SubjectiveFeedbackOverview
                questions={questions}
                responses={responses}
                previousResponses={previousResponses}
                history={subjectiveHistory}
                displayedPlayers={feedbackOverviewPlayers}
                templateName={hooperTemplateName}
                sessionLabel={selectedHooperSession ? formatHooperSessionLabel(selectedHooperSession, Math.max(hooperSessions.findIndex((s) => s.session_key === selectedHooperSession.session_key), 0)) : null}
                selectedDate={hooperDate}
                onSelectDate={setHooperDate}
                isDarkMode={isDarkMode}
                historyLoading={subjectiveHistoryLoading}
                onRequestEdit={() => setSubjectiveViewMode(FEEDBACK_VIEW_EDIT)}
                canEditInputs={!hasLimitedClubUserAccess}
                onRequestReminder={handleHooperReminder}
                isReminderLoading={hooperReminderLoading}
                reminderMessage={hooperReminderMessage}
                reminderError={hooperReminderError}
              />
            )
          ) : (
            loading || subjectiveLoading ? <LoadingIndicator /> : (
              <Card isDark={isDarkMode}>
                <div className="overflow-x-auto">
                  <table className="min-w-[1100px] w-full">
                    <TableHead isDark={isDarkMode}>
                      <Th label={t('table.player')} />
                      <Th label={t('table.position')} />
                      {columnTitles.map((title, index) => <Th key={index} label={title} />)}
                    </TableHead>
                    <tbody>
                      {feedbackOverviewPlayers.length === 0 ? (
                        <tr><td colSpan={questions.length + 2} className="text-center py-6 text-gray-500">{t('common.no_data')}</td></tr>
                      ) : (
                        feedbackOverviewPlayers.map((player) => {
                          const answeredHooper = hasHooperAnswer(player.id);
                          return (
                            <tr key={player.id} className={`border-b transition-colors duration-200 ${isDarkMode ? "border-gray-700" : "border-gray-300"}`}>
                              <td
                                onClick={() => navigateToPlayerDetails(player, "subjective-table")}
                                className={`px-4 py-3 cursor-pointer ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-200/70"}`}
                                title={t('team_overview.open_player_profile', { defaultValue: 'Open player profile' })}
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <span>{player.first_name + " " + player.last_name}</span>
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                    answeredHooper
                                      ? isDarkMode ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                      : isDarkMode ? "bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                                  }`}>
                                    {answeredHooper ? t('hooper.answered') : t('hooper.pending')}
                                  </span>
                                </div>
                              </td>
                              <td
                                onClick={() => navigateToPlayerDetails(player, "subjective-table")}
                                className={`px-4 py-3 text-center cursor-pointer ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-200/70"}`}
                                title={t('team_overview.open_player_profile', { defaultValue: 'Open player profile' })}
                              >
                                {player.position ? t(`positions.${player.position.toLowerCase()}`, { defaultValue: player.position }) : t('common.na')}
                              </td>
                              {renderDynamicColumnData(questions, responses.filter((r) => r.player_id === player.id), player.id, previousResponses.filter((r) => r.player_id === player.id))}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            )
          )}
        </div>

        {/* RPE */}
        <div style={{ display: feedbackTab === "rpe" ? "block" : "none" }} className="space-y-6">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div className="flex flex-wrap items-center gap-3">
                <PositionSelect />
            </div>
            <div className="flex items-center gap-2">
                <RpeSportDayNavigator
                  value={rpeDate}
                  onChange={setRpeDate}
                  isDarkMode={isDarkMode}
                />

              {isRpeSportDayToday(rpeDate) ? (
                <button
                  onClick={() => { if (!isRpeModalOpen) { setRpeModalMode("live"); setIsRpeConfirmOpen(true); } }}
                  disabled={isRpeModalOpen}
                  className="group inline-flex items-center gap-3 rounded-xl px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/30 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 group-hover:bg-white/15 transition-colors">
                    <Gauge className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-xs font-semibold tracking-wide uppercase">{t('rpe.session_label')}</span>
                    <span className="text-[11px] opacity-90">{t('rpe.start_session')}</span>
                  </div>
                </button>
              ) : (
                <button
                  onClick={() => { if (!isRpeModalOpen) { setRpeModalMode("backfill"); setRpeModalOpen(true); } }}
                  disabled={isRpeModalOpen}
                  className="group inline-flex items-center gap-3 rounded-xl px-3 py-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-md shadow-indigo-500/30 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 group-hover:bg-white/15 transition-colors">
                    <History className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-xs font-semibold tracking-wide uppercase">{t('rpe.backfill_session_short', { defaultValue: 'Session passée' })}</span>
                    <span className="text-[11px] opacity-90">{t('rpe.create_and_fill_session', { defaultValue: 'Créer et saisir' })}</span>
                  </div>
                </button>
              )}

              <div className={`inline-flex items-center gap-2 rounded-lg border px-2 py-1 ${isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-white"}`}>
                <span className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                  {t("rpe.session_label", { defaultValue: "RPE" })}
                </span>
                <select
                  value={selectedRpeSessionId || ""}
                  onChange={(e) => handleRpeSessionChange(e.target.value)}
                  className={`min-w-[220px] text-sm rounded px-2 py-1 border ${isDarkMode ? "bg-gray-900 border-gray-600 text-white" : "bg-white border-gray-300 text-black"}`}
                >
                  <option value="">
                    {t("rpe.select_session_placeholder", { defaultValue: "Select a session to edit" })}
                  </option>
                  {mergedRpeSessions.map((session, index) => (
                    <option key={session.session_id} value={session.session_id}>
                      {formatRpeSessionLabel(session, index)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {selectedRpeSession && (
            <div className={`-mt-1 flex flex-wrap items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? "bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"}`}>
                {formatRpeSessionLabel(
                  selectedRpeSession,
                  Math.max(
                    mergedRpeSessions.findIndex(
                      (session) =>
                        normalizeRpeSessionId(session.session_id)
                        === normalizeRpeSessionId(selectedRpeSession.session_id)
                    ),
                    0
                  )
                )}
              </span>
              {(selectedRpeSession.is_open || !selectedRpeSession.closed_at) ? (
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}>
                  {t("rpe.session_open", { defaultValue: "Open" })}
                </span>
              ) : (
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isDarkMode ? "bg-gray-500/15 text-gray-200 ring-1 ring-gray-400/30" : "bg-gray-100 text-gray-700 ring-1 ring-gray-200"}`}>
                  {t("rpe.session_closed", { defaultValue: "Closed" })}
                </span>
              )}
            </div>
          )}

          {selectedRpeSessionId && (
            <div className="flex justify-end">
              <div className={`inline-flex items-center rounded-lg border p-0.5 ${isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-100"}`}>
                <button
                  type="button"
                  onClick={() => setRpeViewMode(FEEDBACK_VIEW_OVERVIEW)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    rpeViewMode === FEEDBACK_VIEW_OVERVIEW
                      ? isDarkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                      : isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t("feedback.overview_mode", { defaultValue: "Overview" })}
                </button>
                <button
                  type="button"
                  onClick={() => setRpeViewMode(FEEDBACK_VIEW_EDIT)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    rpeViewMode === FEEDBACK_VIEW_EDIT
                      ? isDarkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"
                      : isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t("feedback.edit_entry", { defaultValue: "Modification de la saisie" })}
                </button>
              </div>
            </div>
          )}

          {(rpeViewMode !== FEEDBACK_VIEW_EDIT || !selectedRpeSessionId) ? (
            showMainLoader ? <LoadingIndicator /> : (
              <>
                {isSoftLoading && (
                  <div className={`mb-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t('team_overview.refreshing')}</div>
                )}
                <RpeFeedbackOverview
                  rows={rpeData}
                  history={rpeHistory}
                  displayedPlayers={rpeOverviewPlayers}
                  selectedPosition={selectedPosition}
                  activeAlertPlayerIds={activeAlertPlayerIds}
                  coachRpeValue={selectedRpeSession?.coach_rpe_value ?? null}
                  selectedDate={rpeDate}
                  onSelectDate={setRpeDate}
                  isDarkMode={isDarkMode}
                  historyLoading={rpeHistoryLoading}
                  canEditInputs={false}
                  sessions={mergedRpeSessions}
                  selectedSessionId={selectedRpeSessionId}
                  onSelectSession={handleRpeSessionChange}
                  formatSessionLabel={formatRpeSessionLabel}
                  clubId={club_id}
                  onSessionMutated={handleRpeSessionMutated}
                  onRequestBackfill={() => {
                    setRpeModalMode("backfill");
                    setRpeModalOpen(true);
                  }}
                />
              </>
            )
          ) : (
            showMainLoader ? <LoadingIndicator /> : (
              <>
                {isSoftLoading && (
                  <div className={`mb-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t('team_overview.refreshing')}</div>
                )}
                <Card isDark={isDarkMode}>
                  <div className="space-y-4 p-1">
                    <EditableCoachRpeReference
                      value={selectedRpeSession?.coach_rpe_value ?? null}
                      sessionId={selectedRpeSessionId}
                      isDark={isDarkMode}
                      onSaved={(newValue) => {
                        setRpeSessions((previous) =>
                          previous.map((session) =>
                            session.session_id === selectedRpeSessionId
                              ? { ...session, coach_rpe_value: newValue }
                              : session
                          )
                        );
                      }}
                    />
                    <RpeSessionManageBar
                      session={selectedRpeSession}
                      clubId={club_id}
                      isDarkMode={isDarkMode}
                      targetDate={rpeDate}
                      onMutated={handleRpeSessionMutated}
                    />
                    <div className="overflow-x-auto">
                    <table className="min-w-[1100px] w-full">
                      <TableHead isDark={isDarkMode}>
                        <Th label={t('table.player')} />
                        <Th label={t('table.position')} />
                        <Th label={t('rpe.value')} />
                      </TableHead>
                      <tbody>
                        {rpeOverviewPlayers.length === 0 ? (
                          <tr><td colSpan="3" className="text-center py-6 text-gray-500">{t('common.no_data')}</td></tr>
                        ) : (
                          rpeOverviewPlayers
                            .filter((player) => {
                              if (
                                selectedPosition !== "All" &&
                                normalizePlayerPosition(player.position) !== selectedPosition
                              ) {
                                return false;
                              }
                              return true;
                            })
                            .map((player) => {
                              const rpe = rpeData.find((row) => String(row.player_id) === String(player.id)) ?? {
                                player_id: player.id,
                                player_name: `${player.first_name || ""} ${player.last_name || ""}`.trim(),
                                player_position: player.position || "N/A",
                                rpe_value: "N/A",
                                prev_rpe_value: null,
                                feedback_id: null,
                              };
                              const activeRpeSessionId = selectedRpeSessionId ?? rpe.session_id ?? null;

                              return (
                              <tr key={player.id} className={`border-b transition-colors duration-200 ${isDarkMode ? "border-gray-700" : "border-gray-300"}`}>
                                <td
                                  onClick={() => navigateToPlayerDetails(player.id, "rpe-table")}
                                  className={`px-4 py-3 cursor-pointer ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-200/70"}`}
                                  title={t('team_overview.open_player_profile', { defaultValue: 'Open player profile' })}
                                >
                                  {rpe.player_name}
                                </td>
                                <td
                                  onClick={() => navigateToPlayerDetails(player.id, "rpe-table")}
                                  className={`px-4 py-3 text-center cursor-pointer ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-200/70"}`}
                                  title={t('team_overview.open_player_profile', { defaultValue: 'Open player profile' })}
                                >
                                  {rpe.player_position ? t(`positions.${rpe.player_position.toLowerCase()}`, { defaultValue: rpe.player_position }) : t('common.na')}
                                </td>
                                <EditableRpeCell
                                  value={rpe.rpe_value !== "N/A" ? rpe.rpe_value : null}
                                  previousValue={rpe.prev_rpe_value ?? null}
                                  feedbackId={rpe.feedback_id}
                                  feedbackSessionId={rpe.session_id ?? null}
                                  playerId={player.id}
                                  isDark={isDarkMode}
                                  colorClass={getRPEColor(rpe.rpe_value)}
                                  targetDate={rpeDate}
                                  sessionId={activeRpeSessionId}
                                  onSaved={(newVal, newFeedbackId) => {
                                    setRpeData((prev) => {
                                      const playerId = String(player.id);
                                      const nextRow = {
                                        ...rpe,
                                        player_id: player.id,
                                        session_id: activeRpeSessionId,
                                        date: rpeDate,
                                        rpe_value: newVal === null || newVal === undefined ? "N/A" : newVal,
                                        feedback_id:
                                          newVal === null || newVal === undefined
                                            ? null
                                            : (newFeedbackId ?? rpe.feedback_id),
                                      };
                                      const index = prev.findIndex((row) => String(row.player_id) === playerId);
                                      if (index === -1) return [...prev, nextRow];
                                      return prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...nextRow } : row));
                                    });
                                  }}
                                />
                              </tr>
                            );
                            })
                        )}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </Card>
              </>
            )
          )}
        </div>
      </div>

    <div
      style={{ display: (currentTab === "loads" && loadTab === "internal") ? "block" : "none" }}
      className="space-y-6"
    >
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
            {internalViewMode === "table" && <PositionSelect includeGoalkeeper={false} />}
            <RiskModeToggle />
        </div>

        {/* View mode toggle (table ↔ histogram) */}
        <div
          className={`ml-auto inline-flex items-center rounded-lg border p-0.5 ${
            isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-100"
          }`}
        >
          <button
            onClick={() => setInternalViewMode("table")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              internalViewMode === "table"
                ? isDarkMode
                  ? "bg-gray-700 text-white shadow-sm"
                  : "bg-white text-gray-900 shadow-sm"
                : isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <LayoutList className="h-3.5 w-3.5" />
            Table
          </button>
          <button
            onClick={() => setInternalViewMode("histogram")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              internalViewMode === "histogram"
                ? isDarkMode
                  ? "bg-gray-700 text-white shadow-sm"
                  : "bg-white text-gray-900 shadow-sm"
                : isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Histogram
          </button>
        </div>
      </div>

      {/* ─── Histogram view ─── */}
      {internalViewMode === "histogram" && (
        loading ? (
          <LoadingIndicator label={t("team_overview.refreshing")} />
        ) : (
        <InternalLoadHistograms
          players={displayedLoadPlayers}
          isDarkMode={isDarkMode}
          selectedPosition={selectedPosition}
          onPositionChange={setSelectedPosition}
          onPlayerClick={(player) => navigateToPlayerDetails(player, "internal-histogram")}
          season={season}
          visibleMetrics={effectiveInternalVisibleMetrics}
          onVisibleMetricsChange={setInternalVisibleMetrics}
          restrictedMetrics={hasLimitedClubUserAccess}
        />
        )
      )}

      {/* ─── Table view (existing) ─── */}
      {internalViewMode === "table" && (loading ? (
        <LoadingIndicator />
      ) : (
        <div className="flex gap-4">
          {/* Metrics column */}
          <div className="flex-1 space-y-8">
            {/* Trainability */}
              {effectiveInternalVisibleMetrics.has("trainability_index") &&
              displayedPlayersWithTrainability.length > 0 && (
                <>
                <TrainabilityRankingSection
                  players={displayedPlayersWithTrainability}
                  isDarkMode={isDarkMode}
                  onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
                />

                {hasInternalRestMetricsVisible && (
                  <div className="relative -mt-1 pt-4 pb-1">
                    <div
                      className={`h-px ${
                        isDarkMode
                          ? "bg-gradient-to-r from-transparent via-slate-500/80 to-transparent"
                          : "bg-gradient-to-r from-transparent via-slate-400/80 to-transparent"
                      }`}
                    />
                    <div className={`mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                      {t("metrics.rest_metrics", { defaultValue: "Rest metrics" })}
                    </div>
                  </div>
                )}
                </>
              )}

            {/* Mechanical fatigue */}
              {effectiveInternalVisibleMetrics.has("mechanical_fatigue") && (
            <MetricRankingCard
              title="Mechanical Fatigue"
              subtitle="Lower body fatigue & muscle focus"
              icon={AlertTriangle}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) =>
                Number.isFinite(p.mechanical_fatigue)
                  ? p.mechanical_fatigue
                  : null
              }
              formatValue={(v) => `${(v * 100).toFixed(1)}%`}
              min={0}
              max={1}
              thresholds={{ low: 0.3, high: 0.7 }}
              renderExtra={(player) => {
                if (
                  !player.at_risk_muscle_group ||
                  player.at_risk_muscle_group === "None"
                ) {
                  return null;
                }
                const v =
                  typeof player.at_risk_muscle_fatigue === "number"
                    ? player.at_risk_muscle_fatigue
                    : null;

                if (!Number.isFinite(v)) return null;

                let chipCls =
                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ";
                if (v <= 0.3) {
                  chipCls +=
                    "bg-blue-500/10 border-blue-400/40 text-blue-600 dark:text-blue-300";
                } else if (v >= 0.7) {
                  chipCls +=
                    "bg-red-500/10 border-red-400/40 text-red-600 dark:text-red-300";
                } else {
                  chipCls +=
                    "bg-emerald-500/10 border-emerald-400/40 text-emerald-600 dark:text-emerald-300";
                }

                return (
                  <div className="flex flex-wrap gap-1">
                    <span className={chipCls}>
                      <span>{player.at_risk_muscle_group}</span>
                      <span>({player.at_risk_muscle_side || "N/A"})</span>
                      <span className="tabular-nums">
                        {(v * 100).toFixed(1)}%
                      </span>
                    </span>
                  </div>
                );
              }}
              tone="amber"
              getDelta={(p) =>
                safeDelta(
                  p.mechanical_fatigue,
                  p.mechanical_fatigue_prev
                )
              }
              deltaIsPercent={true}
              deltaDecimals={1}
              deltaLabel="vs last session"
              invertDeltaColors
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}

            {/* Intensity & RII side by side */}
              {(effectiveInternalVisibleMetrics.has("intensity_index") || effectiveInternalVisibleMetrics.has("RII")) && (
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
                {effectiveInternalVisibleMetrics.has("intensity_index") && (
              <MetricRankingListCard
                title="Intensity"
                subtitle="Lower body intensity (PT)"
                icon={Activity}
                players={displayedLoadPlayers}
                isDarkMode={isDarkMode}
                getValue={(p) =>
                  Number.isFinite(p.intensity_index)
                    ? p.intensity_index
                    : null
                }
                formatValue={(v) => formatPT(v)}
                tone="indigo"
                getDelta={(p) =>
                  safeDelta(
                    p.intensity_index,
                    p.intensity_index_prev
                  )
                }
                deltaIsPercent={false}
                deltaUnit=" PT"
                deltaDecimals={2}
                deltaLabel="vs last session"
                collapsed={internalIntensityCollapsed}
                onCollapseChange={setInternalIntensityCollapsed}
                onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
              />
              )}

                {effectiveInternalVisibleMetrics.has("RII") && (
              <MetricRankingCard
                title="Relative Intensity Index (RII)"
                subtitle="Session intensity vs last 7 days"
                icon={Flame}
                players={displayedLoadPlayers}
                isDarkMode={isDarkMode}
                getValue={(p) =>
                  Number.isFinite(p.RII) ? p.RII : null
                }
                formatValue={(v) => `${(v * 100).toFixed(1)}%`}
                min={0}
                max={1.2}
                thresholds={{ low: 0.7, high: 1.0 }}
                tone="violet"
                getDelta={(p) => safeDelta(p.RII, p.RII_prev)}
                deltaIsPercent={true}
                deltaDecimals={1}
                deltaLabel="vs last session"
                collapsed={internalIntensityCollapsed}
                onCollapseChange={setInternalIntensityCollapsed}
                onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
              />
              )}
            </div>
            )}

            {/* Mechanical Load & MLI side by side */}
              {(effectiveInternalVisibleMetrics.has("mechanical_load_index") || effectiveInternalVisibleMetrics.has("MLI")) && (
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
                {effectiveInternalVisibleMetrics.has("mechanical_load_index") && (
              <MetricRankingListCard
                title="Mechanical Load"
                subtitle="Lower body mechanical load (PT)"
                icon={Gauge}
                players={displayedLoadPlayers}
                isDarkMode={isDarkMode}
                getValue={(p) =>
                  Number.isFinite(p.mechanical_load_index)
                    ? p.mechanical_load_index
                    : null
                }
                formatValue={(v) => formatPT(v)}
                tone="slate"
                getDelta={(p) =>
                  safeDelta(
                    p.mechanical_load_index,
                    p.mechanical_load_index_prev
                  )
                }
                deltaIsPercent={false}
                deltaUnit=" PT"
                deltaDecimals={2}
                deltaLabel="vs last session"
                collapsed={internalMechanicalCollapsed}
                onCollapseChange={setInternalMechanicalCollapsed}
                onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
              />
              )}

                {effectiveInternalVisibleMetrics.has("MLI") && (
              <MetricRankingCard
                title="Mechanical Load Index (MLI)"
                subtitle="Session load vs last 7 days"
                icon={BarChart3}
                players={displayedLoadPlayers}
                isDarkMode={isDarkMode}
                getValue={(p) =>
                  Number.isFinite(p.MLI) ? p.MLI : null
                }
                formatValue={(v) => `${(v * 100).toFixed(1)}%`}
                min={0}
                max={1.2}
                thresholds={{ low: 0.7, high: 1.0 }}
                tone="emerald"
                getDelta={(p) => safeDelta(p.MLI, p.MLI_prev)}
                deltaIsPercent={true}
                deltaDecimals={1}
                deltaLabel="vs last session"
                collapsed={internalMechanicalCollapsed}
                onCollapseChange={setInternalMechanicalCollapsed}
                onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
              />
              )}
            </div>
            )}
          </div>
        </div>
      ))}

      {/* Sidebar for table mode */}
      {internalViewMode === "table" && !loading && !hasLimitedClubUserAccess && (
        <MetricVisibilitySidebar
          metrics={INTERNAL_METRIC_ITEMS}
          visibleMetrics={internalVisibleMetrics}
          onChange={(next) => { setInternalVisibleMetrics(next); sessionStorage.setItem("internalLoad_visibleMetrics", JSON.stringify([...next])); }}
          allKeys={INTERNAL_METRIC_ITEMS.map((m) => m.key)}
          storageKey="internalLoad_sidebarOpen"
          isDarkMode={isDarkMode}
        />
      )}
    </div>
    <div
      style={{ display: (currentTab === "loads" && loadTab === "external") ? "block" : "none" }}
      className="space-y-6"
    >
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {externalViewMode === "table" && (
            <PositionSelect includeGoalkeeper={false} />
          )}
            <RiskModeToggle />
        </div>

        {/* View mode toggle (table ↔ histogram) */}
        <div
          className={`ml-auto inline-flex items-center rounded-lg border p-0.5 ${
            isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-100"
          }`}
        >
          <button
            onClick={() => setExternalViewMode("table")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              externalViewMode === "table"
                ? isDarkMode
                  ? "bg-gray-700 text-white shadow-sm"
                  : "bg-white text-gray-900 shadow-sm"
                : isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <LayoutList className="h-3.5 w-3.5" />
            Table
          </button>
          <button
            onClick={() => setExternalViewMode("histogram")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              externalViewMode === "histogram"
                ? isDarkMode
                  ? "bg-gray-700 text-white shadow-sm"
                  : "bg-white text-gray-900 shadow-sm"
                : isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Histogram
          </button>
        </div>
      </div>

      {/* ─── Histogram view ─── */}
      {externalViewMode === "histogram" && (
        loading ? (
          <LoadingIndicator label={t("team_overview.refreshing")} />
        ) : (
        <ExternalLoadHistograms
          players={displayedLoadPlayers}
          isDarkMode={isDarkMode}
          selectedPosition={selectedPosition}
          onPositionChange={setSelectedPosition}
          onPlayerClick={(player) => navigateToPlayerDetails(player, "external-histogram")}
          season={season}
          visibleMetrics={externalVisibleMetrics}
          onVisibleMetricsChange={setExternalVisibleMetrics}
        />
        )
      )}

      {/* ─── Table view (existing) ─── */}
      {externalViewMode === "table" && (loading ? (
        <LoadingIndicator />
      ) : (
        <div className="space-y-8">
          <div className="space-y-8">
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
            <MetricRankingListCard
              title="Time on Pitch"
              subtitle="Effective time on field"
              icon={Timer}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) =>
                Number.isFinite(Number(p.time_on_pitch_min)) ? Number(p.time_on_pitch_min) : null
              }
              formatValue={(v) => (v != null ? `${Math.round(v)} min` : "N/A")}
              tone="slate"
              getDelta={(p) => safeDelta(p.time_on_pitch_min, p.time_on_pitch_min_prev)}
              deltaIsPercent={false}
              deltaUnit=" min"
              deltaDecimals={0}
              deltaLabel="vs last session"
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />

            {/* Distance totale */}
            {externalVisibleMetrics.has("global_distance") && (
            <MetricRankingListCard
              title="Total Distance"
              subtitle="Total distance covered (km)"
              icon={Gauge}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) =>
                Number.isFinite(p.global_distance) ? p.global_distance : null
              }
              formatValue={(v) =>
                v !== null
                  ? `${v.toFixed(2)} km`
                  : "N/A"
              }
              tone="slate"
              collapsed={externalCodCollapsed}
              onCollapseChange={setExternalCodCollapsed}
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}
          </div>

          {externalVisibleMetrics.has("hsr_distance_m") && (
          <MetricRankingListCard
            title="HSR"
            subtitle="Distance ≥ 19.6 km/h"
            icon={Activity}
            players={displayedLoadPlayers}
            isDarkMode={isDarkMode}
            getValue={(p) => (Number.isFinite(Number(p.hsr_distance_m)) ? Number(p.hsr_distance_m) : null)}
            formatValue={(v) => formatMeters(v)}
            tone="emerald"
            getDelta={(p) => safeDelta(p.hsr_distance_m, p.hsr_distance_m_prev)}
            deltaIsPercent={false}
            deltaUnit=" m"
            deltaDecimals={0}
            deltaLabel="vs last session"
            onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
          />
          )}

          {(externalVisibleMetrics.has("sprint_max") || externalVisibleMetrics.has("sprint_count")) && (
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
            {externalVisibleMetrics.has("sprint_max") && (
            <MetricRankingListCard
              title="Max Sprint Speed"
              subtitle="Top speed during session (km/h)"
              icon={Activity}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) =>
                Number.isFinite(p.sprint_max) ? p.sprint_max : null
              }
              formatValue={(v) =>
                v !== null
                  ? `${v.toFixed(2)} km/h`
                  : "N/A"
              }
              tone="emerald"
              getDelta={(p) =>
                safeDelta(p.sprint_max, p.sprint_max_prev)
              }
              deltaIsPercent={false}
              deltaUnit=" km/h"
              deltaDecimals={2}
              deltaLabel="vs last session"
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}

            {externalVisibleMetrics.has("sprint_count") && (
            <MetricRankingListCard
              title="Sprints (count)"
              subtitle="Total sprint events"
              icon={Flame}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) => (Number.isFinite(Number(p.sprint_count)) ? Number(p.sprint_count) : null)}
              formatValue={(v) => (v !== null ? `${Math.round(v)}` : "N/A")}
              tone="violet"
              getDelta={(p) => safeDelta(p.sprint_count, p.sprint_count_prev)}
              deltaIsPercent={false}
              deltaUnit=""
              deltaDecimals={0}
              deltaLabel="vs last session"
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}
          </div>
          )}

          {/* Max accelerations */}
          {(externalVisibleMetrics.has("acceleration_max") || externalVisibleMetrics.has("deceleration_max")) && (
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
            {externalVisibleMetrics.has("acceleration_max") && (
            <MetricRankingListCard
              title="Max Accelerations"
              subtitle="Highest acceleration (m/s²)"
              icon={Gauge}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) =>
                Number.isFinite(p.acceleration_max) ? p.acceleration_max : null
              }
              formatValue={(v) =>
                v !== null
                  ? `${v.toFixed(2)} m/s²`
                  : "N/A"
              }
              tone="indigo"
              getDelta={(p) =>
                safeDelta(p.acceleration_max, p.acceleration_max_prev)
              }
              deltaIsPercent={false}
              deltaUnit=" m/s²"
              deltaDecimals={2}
              deltaLabel="vs last session"
              collapsed={externalAccelCollapsed}
              onCollapseChange={setExternalAccelCollapsed}
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}

            {/* Max decelerations */}
            {externalVisibleMetrics.has("deceleration_max") && (
            <MetricRankingListCard
              title="Max Decelerations"
              subtitle="Highest deceleration (m/s²)"
              icon={AlertTriangle}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) =>
                Number.isFinite(p.deceleration_max) ? p.deceleration_max : null
              }
              formatValue={(v) =>
                v !== null
                  ? `${v.toFixed(2)} m/s²`
                  : "N/A"
              }
              tone="amber"
              getDelta={(p) =>
                safeDelta(p.deceleration_max, p.deceleration_max_prev)
              }
              deltaIsPercent={false}
              deltaUnit=" m/s²"
              deltaDecimals={2}
              deltaLabel="vs last session"
              collapsed={externalAccelCollapsed}
              onCollapseChange={setExternalAccelCollapsed}
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}
          </div>
          )}

          {(externalVisibleMetrics.has("accel_count") || externalVisibleMetrics.has("decel_count")) && (
          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">
            {externalVisibleMetrics.has("accel_count") && (
            <MetricRankingListCard
              title="Accelerations (count)"
              subtitle="Total accelerations"
              icon={Gauge}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) => (Number.isFinite(Number(p.accel_count)) ? Number(p.accel_count) : null)}
              formatValue={(v) => (v !== null ? `${Math.round(v)}` : "N/A")}
              tone="indigo"
              getDelta={(p) => safeDelta(p.accel_count, p.accel_count_prev)}
              deltaIsPercent={false}
              deltaUnit=""
              deltaDecimals={0}
              deltaLabel="vs last session"
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}

            {externalVisibleMetrics.has("decel_count") && (
            <MetricRankingListCard
              title="Decelerations (count)"
              subtitle="Total decelerations"
              icon={AlertTriangle}
              players={displayedLoadPlayers}
              isDarkMode={isDarkMode}
              getValue={(p) => (Number.isFinite(Number(p.decel_count)) ? Number(p.decel_count) : null)}
              formatValue={(v) => (v !== null ? `${Math.round(v)}` : "N/A")}
              tone="amber"
              getDelta={(p) => safeDelta(p.decel_count, p.decel_count_prev)}
              deltaIsPercent={false}
              deltaUnit=""
              deltaDecimals={0}
              deltaLabel="vs last session"
              onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
            />
            )}
          </div>
          )}

          

          {/* Distance & COD side by side */}
          {externalVisibleMetrics.has("COD_number") && (
          <MetricRankingListCard
            title="Change of Direction Count"
            subtitle="Total COD events"
            icon={BarChart3}
            players={displayedLoadPlayers}
            isDarkMode={isDarkMode}
            getValue={(p) =>
              Number.isFinite(p.COD_number) ? p.COD_number : null
            }
            formatValue={(v) =>
              v !== null
                ? v.toFixed(0)
                : "N/A"
            }
            tone="violet"
            collapsed={externalCodCollapsed}
            onCollapseChange={setExternalCodCollapsed}
            onPlayerClick={(player) => navigate(`/players/${encodeId(player.id)}`)}
          />
          )}            

          

          
          </div>
        </div>
      ))}

      {/* Sidebar for table mode */}
      {externalViewMode === "table" && !loading && (
        <MetricVisibilitySidebar
          metrics={EXTERNAL_METRIC_ITEMS}
          visibleMetrics={externalVisibleMetrics}
          onChange={(next) => { setExternalVisibleMetrics(next); sessionStorage.setItem("externalLoad_visibleMetrics", JSON.stringify([...next])); }}
          allKeys={EXTERNAL_METRIC_ITEMS.map((m) => m.key)}
          storageKey="externalLoad_sidebarOpen"
          isDarkMode={isDarkMode}
        />
      )}
    </div>
    {currentTab === "loads" && (
      <Card isDark={isDarkMode}>
        <div className="p-4">
          <TeamPerformanceCharts
            key={season}
            selectedPosition={selectedPosition}
            selectedDate={lastDate}
            category={loadTab === "external" ? "external" : "internal"}
            restrictedInternalMetrics={hasLimitedClubUserAccess}
            atRiskChartsOnly={isAtRiskMode}
            overviewLoading={loading}
          />
        </div>
      </Card>
    )}

    {/* ─── GLOBAL STATS TAB (national teams only) - hidden for now ───
    {isNationalTeam && (
      <div style={{ display: currentTab === "global" ? "block" : "none" }} className="space-y-6">
        {currentTab === "global" && <GlobalStatsTab />}
      </div>
    )}
    */}
    <div
            style={{ display: currentTab === "subjective" ? "block" : "none" }}
            className="space-y-6"
          >
      <div className="flex justify-between items-center mb-4">
        <select
          className="p-3 border rounded-lg bg-white text-black text-sm focus:ring focus:ring-blue-300 shadow-md"
          value={selectedPosition}
          onChange={(e) => setSelectedPosition(e.target.value)}
        >
          <option value="All">All Positions</option>
          <option value="Defense">Defense</option>
          <option value="Midfield">Midfield</option>
          <option value="Forward">Forward</option>
          <option value="Lateral">Lateral</option>
        </select>
        <button
    onClick={() => setIsModalOpen(true)}
    className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition"
  >
    ➕ Subjective test
  </button>
      </div>

      {loading ? (
        <LoadingIndicator />
      ) : (
<div
  style={{ display: currentTab === "subjective" ? "block" : "none" }}
  className="space-y-6"
>
  {loading ? (
    <LoadingIndicator />
  ) : (
      <Card isDark={isDarkMode}>
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full">
            <TableHead isDark={isDarkMode}>
            <Th label="Player" />
            <Th label="Position" />
            {/* Dynamically render column titles */}
            {columnTitles.map((title, index) => (
              <Th key={index} label={title} />
            ))}
        </TableHead>
        <tbody>
          {displayedPlayers.length === 0 ? (
            <tr>
              <td colSpan={questions.length + 2} className="text-center py-6 text-gray-500">No data available</td>
            </tr>
          ) : (
            displayedPlayers.map((player) => (
              <tr
                key={player.id}
                className={`border-b transition-colors duration-200 ${isDarkMode ? "border-gray-700" : "border-gray-300"}`}
              >
                <td
                  onClick={() => navigateToPlayerDetails(player, "legacy-subjective-table")}
                  className={`px-4 py-3 cursor-pointer ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-200/70"}`}
                  title={t('team_overview.open_player_profile', { defaultValue: 'Open player profile' })}
                >
                  {player.first_name + " " + player.last_name}
                </td>
                <td
                  onClick={() => navigateToPlayerDetails(player, "legacy-subjective-table")}
                  className={`px-4 py-3 text-center cursor-pointer ${isDarkMode ? "hover:bg-gray-700/70" : "hover:bg-gray-200/70"}`}
                  title={t('team_overview.open_player_profile', { defaultValue: 'Open player profile' })}
                >
                  {player.position || "N/A"}
                </td>
                {/* Dynamically render question answers */}
                {renderDynamicColumnData(questions, responses.filter((response) => response.player_id === player.id), player.id, previousResponses.filter((response) => response.player_id === player.id))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    </Card>
  )}
</div>

      )}
    </div>

      <AppNoticeDialog
        open={isRpeConfirmOpen}
        isDarkMode={isDarkMode}
        mode="confirm"
        variant="send"
        icon="bell"
        title={t("rpe.confirm_title")}
        message={t("rpe.confirm_body")}
        confirmLabel={t("rpe.confirm_start")}
        cancelLabel={t("common.cancel")}
        onCancel={() => setIsRpeConfirmOpen(false)}
        onConfirm={() => {
          setIsRpeConfirmOpen(false);
          setRpeModalMode("live");
          setRpeModalOpen(true);
        }}
      />

      <RPESessionModal
        isOpen={isRpeModalOpen}
        onClose={() => setRpeModalOpen(false)}
        clubId={club_id}
        mode={rpeModalMode}
        targetDate={rpeDate}
        onOpenSurveySettings={() => navigate("/survey")}
        onSessionCreated={({ sessionId }) => {
          setSelectedRpeSessionId(sessionId);
          setRpeViewMode(FEEDBACK_VIEW_OVERVIEW);
        }}
        onSessionsChanged={handleRpeSessionMutated}
      />
    </div>
  );
};

const Th = ({ label, onClick }) => (
  <th
    className={`px-4 py-3 text-center cursor-pointer select-none hover:bg-opacity-80 transition-all ${onClick ? "hover:text-blue-500" : ""}`}
    onClick={onClick}
  >
    {label} {onClick && <ArrowUpDown className="inline ml-1 text-blue-500" />}
  </th>
);

const formatPercentage = (value) => {
  return (value !== undefined && !isNaN(value)) ? `${(value * 100).toFixed(1)}%` : "N/A";
};

const formatPT = (value) => {
  return (value !== undefined && !isNaN(value)) ? `${(value)} PT` : "N/A";
};

const formatMeters = (m) => {
  const v = Number(m);
  if (!Number.isFinite(v)) return "N/A";
  return `${Math.round(v)} m`;
};

export default TeamOverview;
